import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Decimal from 'decimal.js';
import GoogleAPI from '../../utils/GoogleAPI';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
  ButtonGroup,
} from "@chakra-ui/react";

import { WebAPIContext } from '../../contexts/WebAPIContext';

import ReactFlow, {
  ConnectionLineType,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import {
  BomData,
  BomSheetsData,
  CBomType, CBomWithPartNoType,
  ConfirmorInfo,
  Currency,
  CycleTimeInSecondByPartNoType,
  Errors,
  PagePermissions,
  PartNo,
  ProductionLineType,
  ProjectContextType,
  ProjectWithRdsData,
  UniqueProcessType,
} from "../../contexts/types";
import { buildHierarchyProcess, convertCycleTimeInSecondByPartNoLis, drawProcessFlow, fetchProcess, nodeTypes } from "./ProcessFlow";

import { v4 } from 'uuid';
import { CycleTimeUnits, EquipmentUsageType, Messages, NonWorkingItems, PurposeType, ShiftSystem, WorkflowId, codeToNameMap, isSimpleApplicationForm, } from "../../contexts/constants";
import { NonWorkingHoursType, WorkingDaysType } from "../../contexts/types";
import { useAppSelector } from "../../store/hooks";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import ConfirmorList from "./ConfirmorList";
import NonWorkingHours from "./NonWorkingHours";
import WorkingDays from "./WorkingDays";
import HelpIcon from "./HelpIcon";

import { convertToSecond, getFiscalYearsString, getTotalFiscalYearsByProject, getFiscalYearsByProjectDates, isSameFiscalYear, } from '../../utils/calculation';
import { convertCurrency } from '../../utils/CurrencyConverter';
import { getLocalCurrencyId, resolveDisplayPartNo } from '../../utils/utils';
import { convertCurrencyByMonth } from '../../utils/reviewCalculation';

// SpreadSheet同期用カスタムフック
import {
  workingDaysToSpreadSheetData,
  spreadSheetDataToWorkingDays,
  nonWorkingHoursToSpreadSheetData,
  spreadSheetDataToNonWorkingHours,
  getMonthNames,
  type WorkingDaysYearData,
  type NonWorkingHoursYearData,
} from '../../hooks';

// コンテキスト
import dayjs from "dayjs";
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";
import FileUploadModal from "../Modal/FileUploadModal";
import BomInfoList from "./BomInfoList";
import BomCreateModal from "../Modal/BomCreateModal";

/**
 * Confirmor型を定義
 */
interface Confirmor {
  productionProcess?: {
    companyDivision?: string[];
    procurementDivision?: string[];
  };
}

/**
 * 新規投資申請画面(製造工程)パラメータ
 */
interface ProductionProcessProps {
  projectId: string;
  monthlyRateMap?: Map<string, Currency[]>;
}

/**
 * 新規投資申請画面(製造工程)
 * @returns
 */
const ProductionProcess: React.FC<ProductionProcessProps> = ({ projectId, monthlyRateMap }) => {
  /** 多言語対応 */
  const { t } = useTranslation();

  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** 画面タイトル */
  const TITLE = t('features.InvestmentScreening.ProductionProcess.title');

  /** 入力項目名幅 */
  const labelWidth = 160;

  /** 確認ラベルスタイル */
  const confirmLabelStyle = {
    fontWeight: "bold",
    whiteSpace: "normal",
    wordBreak: "break-word",
    width: `${labelWidth}px`,
  };

  /** 確認値スタイル */
  const confirmValueStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }

  /** GoogleAPIクラスオブジェクト */
  const googleAPI: GoogleAPI = new GoogleAPI();

  /** 月名配列（1月始まり）- 表示言語に応じて動的に取得 */
  const MONTH_NAMES = getMonthNames();

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);

  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);

  /** プロジェクト情報 */
  const [projectWithRdsData, setProjectWithRdsData] = useState<ProjectWithRdsData | null>(null);
  /** ローカル通貨ID（会社/工場スナップショットから取得） */
  const localCurrencyId = getLocalCurrencyId(projectWithRdsData?.companyPlantSnapshot);

  /** 通貨マスタ情報 */
  const [currencyData, setCurrencyData] = useState<Currency[]>([]);

  /** パーツNo */
  const [partNoList, setPartNoList] = useState<any[]>([]);

  /** BOMデータ一覧 */
  const [bomDataList, setBomDataList] = useState<BomData[]>([]);

  /** 編集パーツNo */
  const [editPartNo, setEditPartNo] = useState<any[]>([]);

  /** 製造ライン情報 */
  const [productionLine, setProductionLine] = useState<ProductionLineType>({
    productionLineId: "",
    productionLineName: "",
    cycleTime: null,
    workingDays: null,
    nonWorkingHours: null,
    yieldRate: null,
    bestProductionQuantity: null,
    improvementBaseMonthYield: null,
    improvementBaseMonthAvailability: null,
    improvementBaseMonthCycleTime: null,
    updateDate: "",
  });

  /** 一時保存（Draft）フラグ */
  const [hasDraftWorkingDays, setHasDraftWorkingDays] = useState<boolean>(false);
  const [hasDraftNonWorkingHours, setHasDraftNonWorkingHours] = useState<boolean>(false);
  const [hasDraftBaseMonth, setHasDraftBaseMonth] = useState<boolean>(false);

  /** 稼働日数データ(一時保持用) */
  const [tempWorkingDaysData, setTempWorkingDaysData] = useState<{ fiscalYear: string, workingDaysData: WorkingDaysType }[]>([]);

  /** 稼働日数データ */
  const [workingDaysData, setWorkingDaysData] = useState<{ fiscalYear: string, workingDaysData: WorkingDaysType }[]>([]);

  /** 稼働日数データの入力チェック結果 */
  const [isValidWorkingDays, setIsValidWorkingDays] = useState<boolean>(false);

  /** 必須データ検証結果 */
  const [isRequiredDataValid, setIsRequiredDataValid] = useState<boolean>(false);

  /** 非稼働時間データ(一時保持用) */
  const [tempNonWorkingHoursData, setTempNonWorkingHoursData] = useState<{ fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[]>([]);

  /** 非稼働時間データ */
  const [nonWorkingHoursData, setNonWorkingHoursData] = useState<{ fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[]>([]);

  /** 基準月(一時保持用) */
  const [tempBaseMonthYield, setTempBaseMonthYield] = useState<string | null>(null);
  const [tempBaseMonthAvailability, setTempBaseMonthAvailability] = useState<string | null>(null);
  const [tempBaseMonthCycleTime, setTempBaseMonthCycleTime] = useState<string | null>(null);

  /** 稼働日数モーダル初期フォーカス */
  const workingDaysInitialFocusRef = useRef<HTMLButtonElement>(null);

  /**
   * 製造ラインエラー情報を初期化する。
   */
  const clearProductLineErrors = (): Errors => {
    return {
      workingDays: {
        isError: false,
        errorMessage: "",
      },
      nonWorkingHours: {
        isError: false,
        errorMessage: "",
      }
    };
  }

  /**
   * 稼働日数の初期値を生成する。
   */
  const createInitialWorkingDaysData = (fiscalYear: string): { fiscalYear: string, workingDaysData: WorkingDaysType } => {
    return {
      fiscalYear: fiscalYear,
      workingDaysData: {
        factoryWorkingDays: [],
        workingDays: [],
        workOnWeekendAndHolidays: [],
        lineCount: [],
        workOnHolidays: [],
        requiredOverTimes: [],
        sumFactoryWorkingDays: 0,
        sumWorkingDays: 0,
        sumWeekendWorkDays: 0,
        sumHolidayWorkDays: 0,
      }
    };
  };

  /**
   * 非稼働時間の初期値を生成する。
   */
  const createInitialNonWorkingHoursData = (fiscalYear: string): { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] } => {
    const DEFAULT_TIME = "00:00";
    return {
      fiscalYear: fiscalYear,
      nonWorkingHoursByMonth: Array(12).fill(null).map(() => ({
        workTime: DEFAULT_TIME,
        overTime: DEFAULT_TIME,
        shiftSystem: ShiftSystem.OneShiftSystem.code,
        nonWorkingItems: Object.entries(codeToNameMap(NonWorkingItems))
          .map(([code]) => ({
            nonWorkingId: code,
            nonWorkingHour: DEFAULT_TIME,
          })),
        operatingHour: DEFAULT_TIME,
        nonWorkingHour: DEFAULT_TIME,
        workingHours: DEFAULT_TIME,
        availabilityRate: 1,
        unavailabilityRate: 0,
      })),
    };
  };

  /** 製造ラインエラー情報 */
  const [productLineErrors, setProductLineErrors] = useState<Errors>(clearProductLineErrors());

  /** アクティブ スプレッドシートID(workingDays) */
  const [activeSpreadsheetIdByworkingDays, setActiveSpreadsheetIdByworkingDays] = useState<string | null>(null);

  /** アクティブ パーツNo */
  const [activePartNo, setActivePartNo] = useState<string | null>(null);

  /** スプレッドシートウィンドウ */
  const spreadsheetWindow = useRef<Window | null>(null);

  /** スプレッドシートウィンドウ管理（workingDays） */
  const [spreadSheetWindows, setSpreadSheetWindows] = useState<Window | null>(null);

  /** アンマウント時のクローズ用に最新のウィンドウ参照を保持（spreadsheetWindow は未使用のため別途保持） */
  const spreadSheetWindowsRef = useRef<Window | null>(null);
  useEffect(() => {
    spreadSheetWindowsRef.current = spreadSheetWindows;
  }, [spreadSheetWindows]);
  useEffect(() => {
    return () => {
      // 画面遷移時にGoogleSpreadを閉じる
      if (spreadSheetWindowsRef.current && !spreadSheetWindowsRef.current.closed) {
        spreadSheetWindowsRef.current.close();
      }
    };
  }, []);

  /** GoogleSpreadボタン制御 */
  const [isLoading, setIsLoading] = useState(false);

  /** 「データ連携」活性/非活性 */
  const [isEnabledDataSync, setIsEnabledDataSync] = useState<boolean>(false);

  /** 入力モード */
  const [inputMode, setInputMode] = useState<"google" | "manual">("manual");

  /** 非稼働時間データ連携バリデーションエラー */
  const [nonWorkingHoursSyncError, setNonWorkingHoursSyncError] = useState<{
    workingHoursErrors: string[];
    nonWorkingHourErrors: string[];
    invalidValueErrors: string[];
  }>({ workingHoursErrors: [], nonWorkingHourErrors: [], invalidValueErrors: [] });

  /** 稼働日数データ連携バリデーションエラー */
  const [workingDaysSyncError, setWorkingDaysSyncError] = useState<{
    invalidValueErrors: string[];
  }>({ invalidValueErrors: [] });

  /** DBデータあり */
  const [hasRegisteredDBData, setHasRegisteredDBData] = useState<boolean>(false);

  /** プロセス情報 */
  const [uniqueProcessData, setUniqueProcessData] = useState<UniqueProcessType[]>([]);

  /** プロセスフローのノード情報 */
  const [nodes, setNodes, onNodesChange] = useNodesState([]);

  /** プロセスフローのエッジ情報（線） */
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  /** 権限用 画面パーミッション */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();

  /** アップロード可能なファイルの拡張子 */
  const allowedExtensions = ["jpg", "jpeg", "png"];

  // -----------------------------
  // 権限情報
  // -----------------------------
  const { userPermissions, reviewDate } = useContext(ProjectContext) as ProjectContextType;

  /** どの部署の確認かを表す */
  const [confirmSource, setConfirmSource] = useState<string>("");

  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);

  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});

  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    procurementDivision: [],
  });

  /** useRef */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };

  /** モーダル表示制御用：確認モーダル */
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure();

  /** モーダル表示制御用：パーツNo選択モーダル */
  const { isOpen: isOpenSelectPartModal, onOpen: onOpenSelectPartModal, onClose: onCloseSelectPartModal } = useDisclosure();

  /** モーダル表示制御用：稼働日数モーダル */
  const { isOpen: isOpenWorkingDaysModal, onOpen: onOpenWorkingDaysModal, onClose: onCloseWorkingDaysModal } = useDisclosure();

  /** モーダル表示制御用：非稼働時間モーダル */
  const { isOpen: isOpenNonWorkingHoursModal, onOpen: onOpenNonWorkingHoursModal, onClose: onCloseNonWorkingHoursModal } = useDisclosure();

  /** モーダル表示制御用：基準月設定基ーダル */
  const { isOpen: isOpenBaseMonthModal, onOpen: onOpenBaseMonthModal, onClose: onCloseBaseMonthModal } = useDisclosure();

  /** 【ファイルアップロード】モーダル表示制御 */
  const { isOpen: isOpenFileUploadModal, onOpen: onOpenFileUploadModal, onClose: onCloseFileUploadModal } = useDisclosure();

  /** モーダル表示制御用：BOMデータ一覧モーダル */
  const { isOpen: isOpenBomInfoList, onOpen: onOpenBomInfoList, onClose: onCloseBomInfoList } = useDisclosure();

  /** モーダル表示制御用：BOM作成モーダル */
  const { isOpen: isOpenBomCreateModal, onOpen: onOpenBomCreateModal, onClose: onCloseBomCreateModal } = useDisclosure();

  /** BOMデータ一覧データ */
  // const [processBomPreviewData, setProcessBomPreviewData] = useState<ProcessBomPreviewData | null>(null);

  /**
   * 権限情報
   */
  useEffect(() => {
    const pagePermissions = getPermissions(userPermissions, WorkflowId.ProductionProcess, projectWithRdsData?.purpose, employeeId, divisionId, projectWithRdsData?.registrantDivisionId);
    setPagePermissions(pagePermissions);
  }, [userPermissions, employeeId, divisionId, projectWithRdsData]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    const fetchConfirmor = async () => {
      try {
        // 確認担当者JSONを取得
        const confirmorData = await getConfirmor(
          WebAPI,
          projectId,
          reviewDate,
        );
        setConfirmor(confirmorData);
      } catch (error) {
        // TODO 処理失敗時は暫定でスルーする
      }
    };
    if (projectId) {
      fetchConfirmor();
    }
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.productionProcess) {
      const updatedNames = Object.keys(names).reduce((acc, division) => {
        const divisionData = confirmor.productionProcess?.[division as keyof Confirmor['productionProcess']];
        if (Array.isArray(divisionData)) {
          acc[division] = divisionData;
        } else {
          acc[division] = [];
        }
        return acc;
      }, {} as Record<string, ConfirmorInfo[]>);
      setNames(updatedNames);
    }
  }, [confirmor]);

  /**
   * 画面初期表示時に GAPI 初期化
   */
  useEffect(() => {
    /**
     * GAPIロードとサインインを行う。
     */
    const initGapi = async () => {
      try {
        if (!await googleAPI.initGapi()) {
          throw new Error("initGapi error");
        }

        if (! await googleAPI.signIn()) {
          throw new Error("signIn error");
        }

      } catch (error) {
        console.error("Google API initialize error : ", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.googleApiInitFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    if (projectId) {
      // GAPIロードとサインイン
      initGapi();
    } else {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    /**
     * 画面アンロード時
     */
    return () => {
      if (spreadsheetWindow.current && !spreadsheetWindow.current.closed) {
        // スプレッドシートを閉じる
        spreadsheetWindow.current.close();
      }
    };
  }, []);

  /**
   * 初期表示（DBデータ取得）
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const payload = {
          query: `
            query fetchProductionProcessData($projectId: String, $useRDSData: Boolean, $reviewDate: String) {
              getProjectById(projectId: $projectId, useRDSData: $useRDSData, reviewDate: $reviewDate) {
                projectId
                projectStartDate
                projectEndDate
                productionLineId
                registrantDivisionId
                updateDate
                projectStartDate
                moldSalesByProject
                developmentSalesByProject
                equipmentSalesByProject
                purpose
                applicationFormType
                equipmentUsageType
                companyPlantId
                companyPlantSnapshot
              }
              getPartNo(projectId: $projectId, reviewDate: $reviewDate) {
                partNo
                displayPartNo
                quantityAndPrice
                moldSalesByPartNo
                developmentSalesByPartNo
              }
              getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
                partNo
                cycleTimeMonthly
                yieldMonthly
              }
              getCurrencyById(projectId: $projectId) {
                currencyId
                currencyName
                exchangeRate
              }
            }`,
          variables: {
            projectId: projectId,
            useRDSData: true,
            ...(reviewDate && { reviewDate })
          }
        };

        // DB情報取得
        const response = await WebAPI.request(payload);

        // プロジェクト情報取得
        const projectData: ProjectWithRdsData | null = response?.data?.getProjectById;
        if (projectData?.projectId) {
          // プロジェクト情報設定
          setProjectWithRdsData(response.data.getProjectById);
        } else {
          // データ取得失敗の場合
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProductionProcess.message.projectFetchFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });

          console.error(`Failed to call API getProjectById. errors = `, response?.errors);
        }

        let maxCycleTime: number | null = null;
        let yieldRate: string | null = null;

        if (response?.data?.getBomByProjectId) {
          // 初年度
          const projectStartDate = dayjs(projectData?.projectStartDate);
          const firstYearMonth = projectStartDate.format("YYYY-MM");

          const bomRows: BomData[] = response?.data?.getBomByProjectId;

          // BOMデータをStateに保持（パーツNo単位の登録チェックに使用）
          setBomDataList(bomRows);

          const cycleTimeMonthlyList = bomRows.map(row => row.cycleTimeMonthly ? JSON.parse(row.cycleTimeMonthly) : {});
          const yieldMonthlyList = bomRows.map(row => row.yieldMonthly ? JSON.parse(row.yieldMonthly) : {});

          // 最大サイクルタイムと歩留まり算出
          const { maxCycleTime: tempMaxCycleTime, yieldRate: tempYieldRate } = calcCycleTimeAndYield(cycleTimeMonthlyList, yieldMonthlyList, firstYearMonth);
          maxCycleTime = tempMaxCycleTime;
          yieldRate = tempYieldRate;
        }

        // プロジェクトに紐づくパーツNo取得
        if (response?.data?.getPartNo) {
          // パーツNo設定
          const partNoObjList = response.data.getPartNo;
          setPartNoList(partNoObjList);
        } else {
          // データ取得失敗の場合
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProductionProcess.message.partNoFetchFailed'),
            status: 'error',
            duration: 5000,
            isClosable: true,
          });

          console.error(`Failed to call API getPartNo. errors = `, response?.errors);
        }

        // 通貨マスタ情報取得
        if (response?.data?.getCurrencyById) {
          setCurrencyData(response.data.getCurrencyById);
        } else {
          console.error(`Failed to call API getCurrencyById. errors = `, response?.errors);
        }

        // 製造ライン情報取得
        await fetchProductLine(response?.data?.getProjectById?.productionLineId, maxCycleTime, yieldRate, response?.data?.getProjectById?.purpose);

        // プロセスフロー描画（setPartNoListの反映を待たずstateを参照してしまうため、取得直後の値を明示的に渡す）
        drawProcessFlowWithFetch(response?.data?.getPartNo);

        // 必須データ検証
        const capexProjectData: ProjectWithRdsData = response?.data?.getProjectById;
        const capexPartNoData: PartNo[] = response?.data?.getPartNo;
        const isValid = validateRequiredData(capexProjectData, capexPartNoData);
        setIsRequiredDataValid(isValid);

      } catch (error) {
        console.error("データ取得失敗:", error);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.dataFetchFailed'),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    if (projectId) {
      fetchData();
    }
  }, [projectId, toast]);

  /**
   * 製造ライン情報の稼働日数、非稼働時間を更新
   */
  useEffect(() => {
    // 必要なデータが揃っていない場合は処理しない
    if (partNoList.length === 0 || !projectWithRdsData?.projectStartDate) {
      return;
    }

    // 簡易モード時は販売数量・販売単価未入力のため、量産開始日〜EOL予定日から年度数を算出
    const totalFiscalYearsByProject = isSimpleApplicationForm(projectWithRdsData?.purpose, projectWithRdsData?.applicationFormType)
      ? getFiscalYearsByProjectDates(projectWithRdsData.projectStartDate, projectWithRdsData.projectEndDate)
      : getTotalFiscalYearsByProject(
        partNoList,
        projectWithRdsData.moldSalesByProject,
        projectWithRdsData.developmentSalesByProject,
        projectWithRdsData.equipmentSalesByProject
      );

    const fiscalYearsString = getFiscalYearsString(
      projectWithRdsData.projectStartDate,
      totalFiscalYearsByProject
    );

    // 稼働日数の処理（draftがあればdraftを優先）
    const workingDaysSource = productionLine.draftWorkingDays || productionLine.workingDays;
    const oldWorkingDaysData: { fiscalYear: string, workingDaysData: WorkingDaysType }[] = workingDaysSource
      ? JSON.parse(workingDaysSource)
      : [];

    // 各年度について、DBにデータがあればそれを使用、なければ初期値で埋める
    // ※会計年度文字列は表示言語でフォーマットが変わるため、完全一致ではなく年数値で判定する。
    // 一致したデータのfiscalYearは現在の表示言語の文字列に合わせ直す（以後の突き合わせで再びずれないようにする）
    const mergedWorkingDaysData = fiscalYearsString.map((fiscalYear) => {
      const existingData = oldWorkingDaysData.find(item => isSameFiscalYear(item.fiscalYear, fiscalYear));
      return existingData ? { ...existingData, fiscalYear } : createInitialWorkingDaysData(fiscalYear);
    });

    setWorkingDaysData(mergedWorkingDaysData);
    setTempWorkingDaysData(mergedWorkingDaysData);

    // 非稼働時間の処理（draftがあればdraftを優先）
    const nonWorkingHoursSource = productionLine.draftNonWorkingHours || productionLine.nonWorkingHours;
    const oldNonWorkingHoursData: { fiscalYear: string, nonWorkingHoursByMonth: NonWorkingHoursType[] }[] = nonWorkingHoursSource
      ? JSON.parse(nonWorkingHoursSource)
      : [];

    // 各年度について、DBにデータがあればそれを使用、なければ初期値で埋める
    // ※会計年度文字列は表示言語でフォーマットが変わるため、完全一致ではなく年数値で判定する。
    // 一致したデータのfiscalYearは現在の表示言語の文字列に合わせ直す（以後の突き合わせで再びずれないようにする）
    const mergedNonWorkingHoursData = fiscalYearsString.map((fiscalYear) => {
      const existingData = oldNonWorkingHoursData.find(item => isSameFiscalYear(item.fiscalYear, fiscalYear));
      return existingData ? { ...existingData, fiscalYear } : createInitialNonWorkingHoursData(fiscalYear);
    });

    setNonWorkingHoursData(mergedNonWorkingHoursData);
    setTempNonWorkingHoursData(mergedNonWorkingHoursData);
  }, [productionLine.workingDays, productionLine.nonWorkingHours, productionLine.draftWorkingDays, productionLine.draftNonWorkingHours, projectWithRdsData?.projectStartDate, projectWithRdsData?.projectEndDate, projectWithRdsData?.purpose, projectWithRdsData?.applicationFormType, partNoList, projectWithRdsData?.moldSalesByProject, projectWithRdsData?.developmentSalesByProject, projectWithRdsData?.equipmentSalesByProject]);

  /**
   * スプレッドシートウィンドウ変化時
   */
  useEffect(() => {
    if (spreadsheetWindow.current && (!spreadsheetWindow.current.closed)) {
      setIsEnabledDataSync(true);
    } else {
      setIsEnabledDataSync(false);
    }
  }, [spreadsheetWindow.current, spreadsheetWindow.current?.closed]);

  /**
   * 製造ライン情報取得
   * @param productionLineId 製造ラインID
   * @param maxCycleTime 最大サイクルタイム
   * @param yieldRate 歩留まり
   * @returns 製造ライン情報
   */
  const fetchProductLine = async (productionLineId: string, maxCycleTime?: number | null, yieldRate?: string | null, purpose?: string) => {
    try {
      if (!productionLineId) {
        changeProductionLine("cycleTime", maxCycleTime);
        changeProductionLine("yieldRate", yieldRate);
        return;
      }

      const payload = {
        query: `
          query getProductionLineById($projectId: String, $productionLineId: String, $reviewDate: String) {
            getProductionLineById(projectId: $projectId, productionLineId: $productionLineId, reviewDate: $reviewDate) {
              productionLineId
              productionLineName
              cycleTime
              workingDays
              nonWorkingHours
              yieldRate
              bestProductionQuantity
              improvementBaseMonthYield
              improvementBaseMonthAvailability
              improvementBaseMonthCycleTime
              draftWorkingDays
              draftNonWorkingHours
              draftBaseMonth
              updateDate
            }
          }`,
        variables: { projectId, productionLineId, ...(reviewDate && { reviewDate }) }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      // 製造ライン情報取得
      if (response?.data?.getProductionLineById?.productionLineId) {
        const productionLine: ProductionLineType = response.data.getProductionLineById;

        if (maxCycleTime) {
          // 最大サイクルタイムを指定した値で更新
          productionLine.cycleTime = maxCycleTime;
        }

        if (yieldRate) {
          // 歩留まりを指定した値で更新
          productionLine.yieldRate = yieldRate;
        }

        // draftBaseMonthが存在する場合、基準月をドラフトデータで上書き（新製品プロジェクトのみ）
        const isNewProduct = (purpose ?? projectWithRdsData?.purpose) === PurposeType.NewProduct.code;
        if (isNewProduct && productionLine.draftBaseMonth) {
          try {
            const draftBaseMonth = JSON.parse(productionLine.draftBaseMonth);
            productionLine.improvementBaseMonthYield = draftBaseMonth.improvementBaseMonthYield ?? productionLine.improvementBaseMonthYield;
            productionLine.improvementBaseMonthAvailability = draftBaseMonth.improvementBaseMonthAvailability ?? productionLine.improvementBaseMonthAvailability;
            productionLine.improvementBaseMonthCycleTime = draftBaseMonth.improvementBaseMonthCycleTime ?? productionLine.improvementBaseMonthCycleTime;
          } catch (e) {
            console.warn("Failed to parse draftBaseMonth:", e);
          }
          setHasDraftBaseMonth(true);
        } else {
          // 新製品以外の場合、基準月のdraftは無視（基準月をnullクリア）
          if (!isNewProduct) {
            productionLine.improvementBaseMonthYield = null;
            productionLine.improvementBaseMonthAvailability = null;
            productionLine.improvementBaseMonthCycleTime = null;
          }
          setHasDraftBaseMonth(false);
        }

        // draftフラグを設定
        setHasDraftWorkingDays(!!productionLine.draftWorkingDays);
        setHasDraftNonWorkingHours(!!productionLine.draftNonWorkingHours);

        // 製造ライン情報取得設定
        setProductionLine(productionLine);
      } else {
        // データ取得失敗の場合
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.productionLineFetchFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });

        console.error(`Failed to call API getProductionLineById. errors = `, response?.errors);
      }
    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.dataFetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }

  /**
   * BOM情報取得
   * @param projectId プロジェクトID
   * @param reviewDate 振り返り日
   */
  const getBomByProjectId = async (projectId: string, reviewDate: string | null) => {
    try {
      const payload = {
        query: `
        query fetchProductionProcessData($projectId: String, $reviewDate: String) {
          getBomByProjectId(projectId: $projectId, reviewDate: $reviewDate) {
            partNo
            cycleTimeMonthly
            yieldMonthly
          }
        }`,
        variables: {
          projectId: projectId,
          useRDSData: true,
          ...(reviewDate && { reviewDate })
        }
      };

      // DB情報取得
      const response = await WebAPI.request(payload);

      if (response?.data?.getBomByProjectId) {
        const bomRows: BomData[] = response?.data?.getBomByProjectId;

        return bomRows;
      }

    } catch (error) {
      console.error("データ取得失敗:", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.dataFetchFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return null;
    }
  }

  /**
   * 必須データ検証
   *
   * @param {ProjectWithRdsData} capexProjectData T_CEAS_PROJECT データ
   * @param {PartNo[]} capexPartNoData M_CEAS_PART_NO データ
   * @returns {boolean} TRUE: OK FALSE: NG
   */
  const validateRequiredData = (
    capexProjectData: ProjectWithRdsData,
    capexPartNoData: PartNo[]

  ): boolean => {

    const functionName = "validateRequiredData";
    try {

      // 必須データ不足画面名
      let displayNameArray: string[] = [];

      if (
        !capexProjectData                                   // T_CEAS_PROJECT データ
        || !capexProjectData.projectId                      // プロジェクトID
        || !capexProjectData.projectStartDate               // プロジェクト開始日（投資設備検収日）
        || !capexProjectData.companyPlantId                 // 会社/工場ID
      ) {
        displayNameArray.push(t('features.InvestmentScreening.ProductionProcess.validation.basicInfo'));
      }
      // 簡易モード時は販売数量・販売単価の入力をスキップ可能としているため、当該データの必須チェックを省略する
      const isSimple = isSimpleApplicationForm(capexProjectData?.purpose, capexProjectData?.applicationFormType);
      if (!isSimple && (
        capexPartNoData.length === 0                        // M_CEAS_PART_NO データ
        || capexPartNoData.some((p) => !p.quantityAndPrice) // 数量単価JSON
        || !capexProjectData.moldSalesByProject             // 金型費売上（プロジェクト別）
        || !capexProjectData.developmentSalesByProject      // 開発費売上（プロジェクト別）
        || !capexProjectData.equipmentSalesByProject        // 設備費売上（プロジェクト別）
      )) {
        displayNameArray.push(capexProjectData?.equipmentUsageType === EquipmentUsageType.GeneralPurpose.code ? t('features.InvestmentScreening.ProductionProcess.validation.quantityAndPriceGeneral') : t('features.InvestmentScreening.ProductionProcess.validation.quantityAndPriceSpecific'));
      }

      // 必須データチェック
      if (displayNameArray.length !== 0) {

        // エラートースト表示
        displayNameArray.forEach((displayName: string) => {
          toast({
            title: TITLE,
            description: t('features.InvestmentScreening.ProductionProcess.message.registerFromScreen', { screenName: displayName }),
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        })
        return false;
      }

      return true;

    } catch (error) {
      console.error(`${functionName} ERROR:`, error);
      return false;
    }

  };

  /**
   * 指定したプロパティ名の値を更新する。
   * @param propertyName プロパティ名
   * @param newValue 新しい値
   */
  const changeProductionLine = (propertyName: string, newValue: any) => {
    setProductionLine((prevState) => ({
      ...prevState,
      [propertyName]: newValue,
    }));
  }


  /**
   * プロセスフロー描画（DB）
   * @param partNoListOverride 呼び出し直後で`partNoList`ステートがまだ反映されていない場合に使う、取得済みのPartNo一覧（未指定時はstateの`partNoList`を使用）
   */
  const drawProcessFlowWithFetch = async (partNoListOverride?: any[]) => {
    // ユニークプロセス情報取得
    const uniqueProcessData = await fetchProcess(projectId, TITLE, WebAPI, toast, reviewDate);

    // DBデータ有無を設定
    setHasRegisteredDBData((uniqueProcessData?.length || 0) > 0);

    setUniqueProcessDataWithCopy(uniqueProcessData);

    // プロセスフローのノード情報とエッジ情報を算出
    const { nodes, edges } = drawProcessFlow(uniqueProcessData, undefined, undefined, partNoListOverride ?? partNoList);

    setNodes(nodes ?? []);
    setEdges(edges ?? []);
  }

  /**
 * プロセスフロー描画（データ）
 */
  const drawProcessFlowWithParamData = (uniqueProcessData?: UniqueProcessType[]) => {

    convertCycleTimeInSecondByPartNoLis(uniqueProcessData ?? []);

    // プロジェクトに紐づくプロセス情報取得
    const { nodes, edges } = drawProcessFlow(uniqueProcessData, undefined, undefined, partNoList);

    setNodes(nodes ?? []);
    setEdges(edges ?? []);
  }

  /**
* 「BOM編集」ボタン押下 ⇒「パーツNo」リンク押下
* @param partNo パーツNo
*/
  const handleGoogleSpreadClick = async (partNo: string) => {
    try {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      // パーツNo選択モーダルを閉じる
      onCloseSelectPartModal();

      // パーツNoセット
      setActivePartNo(partNo);

      // BOMデータ一覧を開く
      onOpenBomInfoList();
    } catch (e) {
      console.error(`GoogleSpread Button clicked Exception.`, e);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * スプレッドシートにデータを書き込む（初期化込み）
   * @param spreadsheetId スプレッドシートID
   * @param data 書き込むデータ
   */
  const writeSpreadsheetData = async (spreadsheetId: string | null, data: any[][]) => {
    if (!spreadsheetId) return;

    // スプレッドシート情報を取得してsheetIdを得る
    const spreadsheetInfo = await googleAPI.getSpreadSheetInfo(spreadsheetId);
    const sheetId = spreadsheetInfo.sheets?.[0]?.properties?.sheetId ?? 0;

    // 前回設定した書式・入力規則を初期化（列構成変更時に古い設定が残らないようにする。値の書き込みより前に実行）
    await googleAPI.resetSheetFormatAndValidation(spreadsheetId, [sheetId]);

    // セル書式をテキストに設定（日付変換を防ぐ）
    await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [{
      sheetId,
      startRow: 1,
      endRow: 201,
      startCol: 1,
      endCol: 14,
      format: {
        numberFormat: { type: "TEXT" }
      }
    }]);

    // シート名を動的に取得（ロケール対応）
    const sheetName = spreadsheetInfo.sheets?.[0]?.properties?.title ?? GoogleAPI.DEFAULT_SHEET_NAME;

    const emptyValues = Array.from({ length: 201 }, () => Array(14).fill(""));
    await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, emptyValues);
    await googleAPI.spreadSheetSetValue(spreadsheetId, sheetName, 1, 1, data);
  };

  /**
   * workingDays
   * 「画面で入力」ボタン押下
   */
  const handleMaualClick = () => {
    spreadSheetWindows?.close();
  }
  /**
   * workingDays
   * 「GoogleSpread」ボタン押下
   */
  const handleGoogleSpreadClickByworkingDays = async (mode?: "workingDays" | "nonWorkingDays") => {

    if (inputMode === "google") {
      return;
    };
    try {
      setIsLoading(true);

      // ポップアップブロック回避：非同期処理より前にユーザー操作起因でウィンドウを確保
      const newWindow = window.open("", "_blank");

      // Googleアカウント認証
      if (! await googleAPI.signIn()) {
        throw new Error("signIn error");
      }

      let fileName = ""
      let folderPath = "CEAS/SpreadSheet/WorkingDays";

      // 対象ファイル名でスプレッドシートを生成する（既に存在する場合は作成しない）
      if (mode === "workingDays") {
        fileName = t('features.InvestmentScreening.WorkingDays.spreadsheet.fileName')
        folderPath = "CEAS/SpreadSheet/WorkingDays";
      } else if (mode === "nonWorkingDays") {
        fileName = t('features.InvestmentScreening.NonWorkingHours.spreadsheet.fileName')
        folderPath = "CEAS/SpreadSheet/NonWorkingHours";
      }
      let folderId = await googleAPI.createFolderPath(folderPath, null);
      if (!folderId) { folderId = ""; }

      const SpreadsheetId = await googleAPI.createSpreadSheet(fileName, folderId, null);
      setActiveSpreadsheetIdByworkingDays(SpreadsheetId)

      // 事前確保したウィンドウをスプレッドシートに遷移
      if (newWindow) {
        newWindow.location.href = googleAPI.getOpenSpradSheetUrl(SpreadsheetId ?? "");
        if (SpreadsheetId) {
          // スプレッドシートウィンドウを保持
          setSpreadSheetWindows(newWindow);
        }

        // GoogleSpreadシートウィンドウを手動で閉じられた場合
        const interval = setInterval(() => {
          if (newWindow.closed) {
            clearInterval(interval); // 監視を停止
            setInputMode("manual");
          }
        }, 500); // 500ms ごとに監視
      } else {
        console.error("GoogleSpreadシートがブロックされました");
      }
      //**ここからシート内容 */
      if (mode === "workingDays") {
        // 共通関数を使用してシートデータに変換
        const sheetData = workingDaysToSpreadSheetData(tempWorkingDaysData as WorkingDaysYearData[]);
        await writeSpreadsheetData(SpreadsheetId, sheetData);
      } else if (mode === "nonWorkingDays") {
        // 共通関数を使用してシートデータに変換
        const sheetData = nonWorkingHoursToSpreadSheetData(tempNonWorkingHoursData as NonWorkingHoursYearData[]);
        await writeSpreadsheetData(SpreadsheetId, sheetData);
      }

    } catch (error) {
      console.error("Change GoogleSpread Error.", error);
      spreadSheetWindows?.close();
      setInputMode("manual");

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.googleSpreadUnavailable'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * workingDays / nonWorkingDays
    * 「データ連携」ボタン押下
    * @param mode "workingDays" | "nonWorkingDays"
    * @returns
    */
  const handleSyncDataByWorkingDays = async (mode: "workingDays" | "nonWorkingDays") => {
    setIsLoading(true);
    try {
      if (!activeSpreadsheetIdByworkingDays) {
        console.error("スプレッドシートIDがありません");
        return;
      }

      // アクセストークンリフレッシュ
      if (!await googleAPI.ensureValidToken()) {
        googleAPI.signIn();
      }

      // シート名を動的に取得（ロケール対応）
      const sheetName = await googleAPI.getFirstSheetName(activeSpreadsheetIdByworkingDays) ?? GoogleAPI.DEFAULT_SHEET_NAME;

      let activeRange = ""
      if (mode === "workingDays") {
        activeRange = `${sheetName}!B1:M`;
      } else if (mode === "nonWorkingDays") {
        activeRange = `${sheetName}!B2:M`;
      }

      // スプレッドシートから値を取得
      const result = await googleAPI.spreadSheetGetValues(activeSpreadsheetIdByworkingDays, activeRange);
      // ここからデータ書き込み
      if (mode === "workingDays") {
        convertSheetToSelectedProcessesByworkingDays(result.values ?? []);
      } else if (mode === "nonWorkingDays") {
        convertSheetToSelectedProcessesByNonWorkingDays(result.values ?? []);
      }
    } catch (error) {
      console.error("データ連携エラー:", error);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * workingDays
   * Google Spreadシートデータを変換する。
   * 共通関数を使用してデータ変換とバリデーションを行う。
   */
  const convertSheetToSelectedProcessesByworkingDays = (values: string[][]) => {
    // 共通関数を使用してデータ変換
    const result = spreadSheetDataToWorkingDays(values, tempWorkingDaysData as WorkingDaysYearData[]);

    // データ更新
    setTempWorkingDaysData(result.data);

    // バリデーションエラーをstateに設定
    setWorkingDaysSyncError({ invalidValueErrors: result.errors });
  }

  /**
   * nonWorkingDays
   * Google Spreadシートデータを変換する。
   * 共通関数を使用してデータ変換とバリデーションを行う。
   */
  const convertSheetToSelectedProcessesByNonWorkingDays = (values: string[][]) => {
    // 共通関数を使用してデータ変換
    const result = spreadSheetDataToNonWorkingHours(values, tempNonWorkingHoursData as NonWorkingHoursYearData[]);

    // データ更新
    setTempNonWorkingHoursData(result.data);

    // バリデーションエラーをstateに設定
    setNonWorkingHoursSyncError(result.errors);
  }

  /**
   * 稼働日数モーダルが閉じられたとき
   */
  const handleCloseCompleteInputModal = async () => {
    // 入力モードを画面に戻す。
    setInputMode("manual");

    // バリデーションエラーをリセット
    setNonWorkingHoursSyncError({ workingHoursErrors: [], nonWorkingHourErrors: [], invalidValueErrors: [] });
    setWorkingDaysSyncError({ invalidValueErrors: [] });

    // スプレッドシートウィンドウを閉じる。（spreadsheetId未設定時も開いているウィンドウは閉じる）
    if (spreadSheetWindows && !spreadSheetWindows.closed) {
      spreadSheetWindows.close();
    }
  }

  /**
   * 非稼働時間の値が変更されたときのコールバック
   * 該当する年度・月・フィールドタイプのエラーメッセージをクリアする
   * @param fiscalYear 年度
   * @param monthIndex 月インデックス
   * @param fieldType フィールドタイプ（workingHours: 労働時間関連, nonWorkingHours: 非稼働時間関連, other: その他）
   */
  const handleNonWorkingHoursValueChange = (fiscalYear: string, monthIndex: number, fieldType: 'workingHours' | 'nonWorkingHours' | 'other') => {
    const location = `${fiscalYear} ${MONTH_NAMES[monthIndex]}`;

    setNonWorkingHoursSyncError(prev => ({
      // 労働時間関連の変更時のみworkingHoursErrorsをクリア
      workingHoursErrors: fieldType === 'workingHours'
        ? prev.workingHoursErrors.filter(err => !err.startsWith(location))
        : prev.workingHoursErrors,
      // 非稼働時間関連の変更時のみnonWorkingHourErrorsをクリア
      nonWorkingHourErrors: fieldType === 'nonWorkingHours'
        ? prev.nonWorkingHourErrors.filter(err => !err.startsWith(location))
        : prev.nonWorkingHourErrors,
      // invalidValueErrorsは該当する年度・月のエラーをクリア
      invalidValueErrors: prev.invalidValueErrors.filter(err => !err.startsWith(location)),
    }));
  }

  /**
   * 編集パーツNoを追加する
   * @param partNo パーツNo
   */
  const changeEditPartNo = (partNo: string) => {
    // 編集したパーツNoを登録・更新用に保持
    setEditPartNo((prev) => {
      if (prev.includes(partNo)) {
        return prev;
      }

      return [...prev, partNo];
    });
  }

  /**
   * 指定したユニークプロセス情報をコピーしてプロセス情報に設定する。
   * @param uniqueProcessData ユニークプロセス情報
   * @returns
   */
  const setUniqueProcessDataWithCopy = (uniqueProcessData?: UniqueProcessType[]) => {
    if (!uniqueProcessData) {
      return;
    }

    const copyData: UniqueProcessType[] = JSON.parse(JSON.stringify(uniqueProcessData));
    copyData.map(u => {
      if (u.uniqueProcessCycleTimeInSecondByPartNo) {
        u.uniqueProcessCycleTimeInSecondByPartNoList = JSON.parse(u.uniqueProcessCycleTimeInSecondByPartNo);
      }
    });

    // プロセス情報を保持(subProcessが設定される前のオブジェクトを設定)
    setUniqueProcessData(copyData);
  }

  /**
   * 「事業部確認」ボタン押下処理
   */
  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    // フォーカスを外す
    event.currentTarget.blur();

    if (!validateProductLine()) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.inputError'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // どの部署からの確認かを保持
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);

    if (!projectWithRdsData) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.registerFromBasicInfo'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (!uniqueProcessData || uniqueProcessData.length === 0) {
      toast({
        title: TITLE,
        description: <>{t('features.InvestmentScreening.ProductionProcess.message.registerBomInfo')}</>,
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // [新規] パーツNo単位のBOM未登録チェック
    // bomDataList に登録済みのパーツNoセットを構築
    const bomRegisteredPartNoSet = new Set<string>(
      bomDataList
        .map(bom => bom.partNo)
        .filter((partNo): partNo is string => !!partNo)
    );
    // partNoList の中でBOM未登録のパーツNoを抽出
    const unregisteredPartNos = partNoList
      .map(p => p.partNo as string)
      .filter(partNo => partNo && !bomRegisteredPartNoSet.has(partNo));

    if (unregisteredPartNos.length > 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.bomNotRegisteredPartNo', {
          partNos: unregisteredPartNos.join('、')
        }),
        status: 'warning',
        duration: 8000,
        isClosable: true,
      });
      return;
    }

    // 確認モーダル表示
    onOpenConfirmModal();
  }

  /**
   * 確認モーダルで「保存」ボタン押下
   * @param event イベント情報
   */
  const handleConfirm: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    // フォーカスを外す
    event.currentTarget.blur();

    try {
      // 製造ライン
      const updateProductLineResult = await updateProductLine();

      if (updateProductLineResult === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        console.warn(`Conflict error. putProductionLine.`);
        return;
      } else if (!updateProductLineResult || updateProductLineResult !== "success") {
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.productionLineSaveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        console.error(`Failed to call API putProductionLine.`);
        return;
      }

      // 製造ライン情報取得
      await fetchProductLine(projectWithRdsData?.productionLineId!);

      // draftフラグをリセット（確定保存により下書きがクリアされた）
      setHasDraftWorkingDays(false);
      setHasDraftNonWorkingHours(false);
      setHasDraftBaseMonth(false);

      // ワークフローテーブルを更新
      await updateConfirm(
        WebAPI,
        projectId,
        WorkflowId.ProductionProcess,
        employeeId,
        confirmSource,
        "true",
        reviewDate,
      );

      // プロセスフロー描画
      await drawProcessFlowWithFetch();

      // 確認担当者JSONを取得
      const confirmor = await getConfirmor(
        WebAPI,
        projectId,
        reviewDate,
      );
      setConfirmor(confirmor);
    } catch (error) {
      console.error(`handleConfirm exception = `, error);
    }

    toast({
      title: TITLE,
      description: t('features.InvestmentScreening.ProductionProcess.message.productionLineSaveSuccess'),
      status: 'success',
      duration: 5000,
      isClosable: true,
    });

    if (editPartNo.length > 0) {
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.processMasterSaveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.partNoProcessSaveSuccess'),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }

    setEditPartNo([]);

    onCloseConfirmModal();
  }

  /**
   * バリデーションチェックを行う。
   */
  const validateProductLine = () => {
    let isError = false;

    const errors: Errors = clearProductLineErrors();

    // 稼働日数
    if (
      !workingDaysData ||                 // データが存在しない
      !Array.isArray(workingDaysData) ||  // 配列でない
      workingDaysData.length === 0        // 配列が空
    ) {
      errors.workingDays.isError = true;
      errors.workingDays.errorMessage = t('features.InvestmentScreening.ProductionProcess.validation.requiredWorkingDays');
      isError = true;
    }

    // 非稼働時間
    if (
      !nonWorkingHoursData ||                 // データが存在しない
      !Array.isArray(nonWorkingHoursData) ||  // 配列でない
      nonWorkingHoursData.length === 0        // 配列が空
    ) {
      errors.nonWorkingHours.isError = true;
      errors.nonWorkingHours.errorMessage = t('features.InvestmentScreening.ProductionProcess.validation.requiredNonWorkingHours');
      isError = true;
    }

    setProductLineErrors(errors);

    return !isError;
  };

  /**
   * 製造ライン情報を保存する。
   */
  const updateProductLine = async (): Promise<string | null> => {
    try {

      if (!productionLine.productionLineId) {
        // フロント側で採番
        productionLine.productionLineId = `pl-${v4()}`;
      }

      // 登録パラメータ
      const input = {
        projectId: projectId,
        productionLineId: productionLine.productionLineId,
        productionLineName: productionLine.productionLineName,
        cycleTime: productionLine.cycleTime,
        workingDays: JSON.stringify(workingDaysData),
        nonWorkingHours: JSON.stringify(nonWorkingHoursData),
        yieldRate: productionLine.yieldRate,
        bestProductionQuantity: productionLine.bestProductionQuantity,
        improvementBaseMonthYield: productionLine.improvementBaseMonthYield || null,
        improvementBaseMonthAvailability: productionLine.improvementBaseMonthAvailability || null,
        improvementBaseMonthCycleTime: productionLine.improvementBaseMonthCycleTime || null,
        updateDate: productionLine.updateDate,
        ...(reviewDate && { reviewDate }),
      };

      // AppSync
      const result = await WebAPI.request({
        query: `
            mutation putProductionLine($input: productionLineInput!) {
              putProductionLine(input: $input) {status}
            }
          `,
        variables: {
          input: input,
        }
      });

      console.log(`putProductionLine result = `, result);
      return result?.data?.putProductionLine?.status;
    } catch (error) {
      console.error("Failed to call API putProductionLine. exception =", error);
      return null;
    }
  }

  /**
   * 一時保存（Draft）をDBに保存する。
   * @param draftType "workingDays" | "nonWorkingHours" | "baseMonth"
   * @param draftData JSON文字列
   */
  const saveDraft = async (draftType: string, draftData: string) => {
    try {
      const input = {
        projectId: projectId,
        productionLineId: productionLine.productionLineId || null,
        productionLineName: productionLine.productionLineName || "",
        draftType: draftType,
        draftData: draftData,
        ...(reviewDate && { reviewDate }),
      };

      const result = await WebAPI.request({
        query: `
          mutation putProductionLineDraft($input: productionLineDraftInput!) {
            putProductionLineDraft(input: $input) { status, id }
          }
        `,
        variables: { input },
      });

      console.log(`putProductionLineDraft result =`, result);

      const response = result?.data?.putProductionLineDraft;
      if (response?.status === "success") {
        // 新規採番された場合、productionLineIdをstateに反映
        if (!productionLine.productionLineId && response.id) {
          setProductionLine((prev) => ({ ...prev, productionLineId: response.id }));
        }
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.draftSaveSuccess'),
          status: "info",
          duration: 2000,
          isClosable: true,
        });
      } else {
        console.error(`putProductionLineDraft failed:`, response);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.draftSaveFailed'),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Failed to call API putProductionLineDraft. exception =", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.draftSaveFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * 一時保存データを破棄し、確定データで再表示する。
   * @param draftType "workingDays" | "nonWorkingHours" | "baseMonth"
   */
  const discardDraft = async (draftType: string) => {
    try {
      if (!productionLine.productionLineId) return;

      const input = {
        projectId: projectId,
        productionLineId: productionLine.productionLineId,
        draftType: draftType,
        draftData: null,
        ...(reviewDate && { reviewDate }),
      };

      const result = await WebAPI.request({
        query: `
          mutation putProductionLineDraft($input: productionLineDraftInput!) {
            putProductionLineDraft(input: $input) { status }
          }
        `,
        variables: { input },
      });

      const response = result?.data?.putProductionLineDraft;
      if (response?.status === "success") {
        // 製造ライン情報を再取得（確定データで再表示）
        await fetchProductLine(productionLine.productionLineId);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.draftDiscardSuccess'),
          status: "info",
          duration: 2000,
          isClosable: true,
        });
      } else {
        console.error(`discardDraft failed:`, response);
        toast({
          title: TITLE,
          description: t('features.InvestmentScreening.ProductionProcess.message.draftDiscardFailed'),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Failed to discard draft. exception =", error);
      toast({
        title: TITLE,
        description: t('features.InvestmentScreening.ProductionProcess.message.draftDiscardFailed'),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
  * ユニークプロセス情報を登録・更新する。
  * @param uniqueProcessData ユニークプロセス情報
  * @returns
  */
  const putUniqueProcess = async (uniqueProcessData: UniqueProcessType[]): Promise<string | null> => {
    try {
      const input = uniqueProcessData.map((u, i) => ({
        uniqueProcessId: u.uniqueProcessId,
        uniqueProcessName: u.uniqueProcessName,
        ...(reviewDate && { reviewDate }),
      }));

      console.log(`input = `, input);

      const response = await WebAPI.request({
        query: `
        mutation putUniqueProcess($input: [UniqueProcessInput]!) {
          putUniqueProcess(input: $input) {status}
        }
      `,
        variables: {
          input: input,
        }
      });

      console.log(`putUniqueProcess result = `, response);
      return response?.data?.putUniqueProcess?.status;
    } catch (error) {
      console.error("Failed to call API putProductionLine. exception =", error);
      return null;
    }
  }

  /**
   * プロジェクト・パーツNo・プロセスID紐づけテーブルに登録・更新する。
   * PartNo単位でAPIを呼び出す（AppSync 1MB制限対策）
   * @param projectId プロジェクトID
   * @param partNo パーツNo配列
   * @param uniqueProcessData ユニークプロセス情報
   */
  const putPartNoAndProcess = async (projectId: string, partNo: string[], uniqueProcessData: UniqueProcessType[]): Promise<string | null> => {
    try {
      // PartNo単位でAPIを呼び出す
      for (const p of partNo) {
        // 対象パーツNoに紐づくプロセスIDを取得
        const processList = uniqueProcessData.filter(u => {
          // 対象パーツNoで抽出
          return u.uniqueProcessCycleTimeInSecondByPartNoList?.some(t => t.partNo === p)
        }).map(u => {
          return {
            processId: u.uniqueProcessId,
            seq: u.uniqueProcessSeq,
            cycleTimeInSecondByPartNo: u.uniqueProcessCycleTimeInSecondByPartNo,
            level: u.uniqueLevel,
          }
        });

        // パラメータ設定（PartNo単位）
        const input = {
          projectId: projectId,
          partNo: p,
          processInfo: processList,
          ...(reviewDate && { reviewDate }),
        };

        console.log(`putPartNoAndProcessByPartNo input (partNo=${p}) = `, input);

        const response = await WebAPI.request({
          query: `
          mutation putPartNoAndProcessByPartNo($input: PartNoAndProcessByPartNoInput!) {
            putPartNoAndProcessByPartNo(input: $input) {status}
          }
        `,
          variables: {
            input: input,
          }
        });

        const result = response?.data?.putPartNoAndProcessByPartNo?.status;
        console.log(`putPartNoAndProcessByPartNo response (partNo=${p}) = `, response);

        if (result !== "success") {
          console.error(`putPartNoAndProcessByPartNo failed for partNo=${p}`);
          return null;
        }
      }

      return "success";

    } catch (error) {
      console.error("Failed to call API putPartNoAndProcessByPartNo. exception =", error);
      return null;
    }
  }

  /**
   * CBOMデータを検証する。
   * @param cbomData CBOMデータ
   * @returns true：正常、false：異常
   * ※詳細はConsole
   */
  const validCBomData = (cbomData: CBomType[]) => {
    if (!cbomData) {
      return false;
    }

    let prevLevel = -1;
    // for (const cbom of cbomData) {
    let i = 0;
    for (i = 0; i < cbomData.length; i++) {
      const cbom = cbomData[i];

      if (!cbom) {
        console.warn(`invalid data [${JSON.stringify(cbom)}]`);
        continue;
      }

      if ((cbom.seq ?? "") === "") {
        // 「Seq.」が空 ⇒ Seqが空のデータは対象外
        continue;
      }

      if ((cbom.seq || 0) < 0) {
        console.warn(`「Seq.」 is negative value. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      }

      // 明示的に文字列に変換
      cbom.level = cbom.level + "";

      if ((cbom.level ?? "") === "") {
        // 「Level」が空
        console.warn(`「Level」 is empty. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      } else if (Number((cbom?.level ?? "").replace(/^-$/, "0").replace(/(\*|-)+/, "")) < 0) {
        console.warn(`「Level」 is negative value. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      }

      if ((cbom.processCode ?? "") === "") {
        if ((cbom.modelNo ?? "") === "" && (cbom.unitTime ?? "") !== "") {
          // 「Process Code」と「Model No.」が空
          console.warn(`「Process Code」 and 「Model No.」 is empty. invalid data [${JSON.stringify(cbom)}]`);
          return false;
        } else {
          // 「Process Code」が空の場合は、エラーとせず、ダミーコードを採番する。
          cbom.processCode = generateProcessCode(cbom);
        }
      }

      if ((cbom.itemNo ?? "") === "") {
        // 「Item No.」が空
        console.warn(`「Item No」 is empty. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      }

      const level = Number(cbom.level === "-" ? "00" : (cbom.level ?? "").replace(/(\*|-)+/, ""));

      if (cbom.level !== "-" && isNaN(level)) {
        console.warn(`format of 「Level」 is invalid. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      }

      if (prevLevel < 0) {
        prevLevel = level;
        continue;
      }

      const diff = level - prevLevel;

      if (diff > 1) {
        // １つ前＋２以上は異常
        console.warn(`order of 「Level」 is invalid. invalid data [${JSON.stringify(cbom)}]`);
        return false;
      }

      prevLevel = level;
    }

    return true;
  }

  /**
   * CBOMファイルを読み込んでオブジェクト配列として返す。
   * @param buffer CBOMファイルバッファ
   * @returns CBOMデータ
   */
  const convertCBom = (rows: string[][]): CBomType[] => {
    // ヘッダ部
    const cellPositions = getCellPositions(rows[1]);

    // タイトル部＋ヘッダ部を切り捨てたものをデータ部として扱う
    const dataRows = rows.slice(2);

    // データ部をCBOM型配列に変換(seqでソート含む)
    const cbomData: CBomType[] = dataRows.map((row, idx) => {
      try {
        const result: CBomType = {
          seq: Number(row[cellPositions.seq] || NaN),
          level: String(row[cellPositions.level] ?? ""),
          modelNo: String(row[cellPositions.modelNo] || ""),
          processCode: String(row[cellPositions.processCode] || ""),
          itemNo: String(row[cellPositions.itemNo] || ""),
          itemName: String(row[cellPositions.itemName] || ""),
          usage: String(row[cellPositions.usage] || ""),
          unitPrice: Number(row[cellPositions.unitPrice] || NaN),
          amount: Number(row[cellPositions.amount] || NaN),
          unitTime: String(row[cellPositions.unitTime] || ""),
        }

        if (isNaN(result.seq ?? NaN)) {
          result.seq = null;
        }

        if (isNaN(result.unitPrice ?? NaN)) {
          result.unitPrice = null;
        }

        if (isNaN(result.amount ?? NaN)) {
          result.amount = null;
        }

        // undefined or null ⇒ 空文字列とスペース除去
        result.level = (result.level || null)?.trim();
        result.modelNo = (result.modelNo || null)?.trim();
        result.processCode = (result.processCode || null)?.trim();
        result.itemNo = (result.itemNo || null)?.trim();
        result.itemName = (result.itemName || null)?.trim();
        result.usage = (result.usage || null)?.trim();
        result.unitTime = (result.unitTime || null)?.trim();

        return result;
      } catch (ex) {
        throw new Error(`Failed to parse the row data. row = [${JSON.stringify(row)}, error = ${JSON.stringify(ex)}]`);
      }
    }).filter(cbom => cbom && cbom.seq).sort((a, b) => (a?.seq ?? 0) - (b?.seq ?? 0));

    return cbomData;
  }

  /**
   * CBOMデータ ⇒ UniqueProcess変換
   * @param cbomDataWithPartNo CBOMデータとパーツNo
   * @param projectId プロジェクトID
   * @returns UniqueProcessデータ
   */
  const convertUniqueProcess = (cbomDataWithPartNo: CBomWithPartNoType, projectId: string): UniqueProcessType[] => {
    // ユニットタイム（Unit Time）ありのみにする ※Unit Timeを必須にするとCBOMファイルの時に困るのでチェックしない
    const filteredData = cbomDataWithPartNo.cbomData.filter(cbom => (cbom?.unitTime ?? "") !== "" && (cbom.seq || null));

    const result = filteredData.map((cbom, idx) => {
      const processInfo: UniqueProcessType = {
        uniqueProcessId: cbom?.processCode,
        uniqueProcessName: cbom?.itemNo,
        uniqueProcessSeq: cbom?.seq ?? 0,
        uniqueLevel: Number((cbom?.level ?? "").replace(/^-$/, "0").replace(/(\*|-)+/, "")),
        uniqueProcessCycleTimeInSecondByPartNo: JSON.stringify([{
          partNo: cbomDataWithPartNo.partNo,
          cycleTime: cbomDataWithPartNo.cycleTimeUnits === CycleTimeUnits.Seconds.code ? cbom?.unitTime : convertToSecond(cbom?.unitTime || "0"),
          itemName: cbom?.itemName,
        }]),
        posX: 0,
        posY: 0,
      }
      return processInfo;
    });

    return result;
  }

  /**
   * 指定したパーツNoのプロセスを削除する。
   * @param uniqueProcessData プロセス情報
   * @param partNo 削除対象パーツNo
   * @returns 削除後のプロセス情報
   */
  const removeProcess = (uniqueProcessData: UniqueProcessType[], partNo: string): UniqueProcessType[] => {
    const result = uniqueProcessData.filter(u => {
      if (!u.uniqueProcessCycleTimeInSecondByPartNoList && u.uniqueProcessCycleTimeInSecondByPartNo) {
        // JSON.Parseされていない場合、JSON.Parse
        u.uniqueProcessCycleTimeInSecondByPartNoList = JSON.parse(u.uniqueProcessCycleTimeInSecondByPartNo!);
      }

      // サイクルタイム/PartNoから削除対象パーツNoを含めないよう抽出
      u.uniqueProcessCycleTimeInSecondByPartNoList = u.uniqueProcessCycleTimeInSecondByPartNoList?.filter(t => {
        const b1 = t.partNo !== partNo;
        const b2 = partNoList.some(p => p.partNo === t.partNo);
        return b1 && b2;
      });

      // 上記をサイクルタイム/PartNo（JSON）へ反映
      u.uniqueProcessCycleTimeInSecondByPartNo = JSON.stringify(u.uniqueProcessCycleTimeInSecondByPartNoList);

      // 抽出した情報のうち、サイクルタイム/PartNoが 0 件以外を抽出
      return u.uniqueProcessCycleTimeInSecondByPartNoList && u.uniqueProcessCycleTimeInSecondByPartNoList.length > 0;
    });

    return result;
  }
  /**
   * ２つのデータをマージする。
   * @param baseData ベースとなるデータ
   * @param additionalData 追加する側のデータ
   * @param newPartNo パーツNo
   * @param projectId プロジェクトID
   * @returns マージしたデータ
   */
  const mergeUniqueProcess = (baseData: UniqueProcessType[], additionalData: UniqueProcessType[], newPartNo: string, projectId: string) => {

    if (!baseData || baseData.length === 0) {
      // ベースがない場合は、seqだけ振り直してそのまま返す。
      additionalData.forEach((p, idx) => {
        p.uniqueProcessSeq = idx + 1;
      });
      return additionalData;
    }

    /**
     * サイクルタイム/パーツNoをマージする。
     * @param baseCycleTimePartNo サイクルタイム/パーツNo（既存データ側）
     * @param addCycleTimePartNo サイクルタイム/パーツNo（BOMファイル側）
     * @returns マージしたサイクルタイム/パーツNo
     */
    const mergePartNo = (baseCycleTimePartNo: string, addCycleTimePartNo: string) => {
      const cycleTimePartNoArray1: CycleTimeInSecondByPartNoType[] = JSON.parse(addCycleTimePartNo);
      let cycleTimePartNoArray2: CycleTimeInSecondByPartNoType[] = JSON.parse(baseCycleTimePartNo);
      const cycleTimePartNo: CycleTimeInSecondByPartNoType | undefined = cycleTimePartNoArray2?.find((t: any) => t.partNo === newPartNo);

      if (cycleTimePartNo) {
        // 存在するなら、サイクルタイム更新
        cycleTimePartNo.cycleTime = cycleTimePartNoArray1[0].cycleTime;
      } else {
        // 存在しないなら、追加
        cycleTimePartNoArray2 = cycleTimePartNoArray2.concat(cycleTimePartNoArray1[0]);
      }

      // パーツNoでソート
      cycleTimePartNoArray2.sort((a, b) => a.partNo.localeCompare(b.partNo));

      return JSON.stringify(cycleTimePartNoArray2);
    }

    /**
     * 検索対象プロセスの親プロセスを検索する。
     * @param targetData 検索対象データ
     * @param targetProcess 検索対象プロセス
     * @returns 検索対象プロセスの親プロセス
     */
    const getParentProcess = (targetData: UniqueProcessType[], targetProcess: UniqueProcessType) => {
      const index = targetData.findIndex(p => p.uniqueProcessId === targetProcess.uniqueProcessId);

      const targetLevel = (targetProcess.uniqueLevel ?? 0);
      for (let i = index; i >= 0; i--) {
        if ((targetData[i].uniqueLevel ?? 0) === targetLevel - 1) {
          return targetData[i];
        }
      }
    }

    // 孫の数算出処理
    const getSubProcessCount = (subProcess: UniqueProcessType[] | null | undefined): number => {
      let cnt = 0;

      subProcess?.forEach((s => {
        cnt += (s.subProcess?.length || 0);
        cnt += getSubProcessCount(s.subProcess);
      }));

      return cnt;
    }

    const minBaseSeq = baseData.reduce((min, item) => Math.min(min, item.uniqueProcessSeq), Infinity);
    const minAdditionalSeq = additionalData.reduce((min, item) => Math.min(min, item.uniqueProcessSeq), Infinity);

    // seqをシーケンシャルに振り直す。
    baseData.forEach((p, idx) => { p.uniqueProcessSeq = minBaseSeq + idx });
    additionalData.forEach((p, idx) => { p.uniqueProcessSeq = minAdditionalSeq + idx });

    // ベースをコピー
    let result: UniqueProcessType[] = JSON.parse(JSON.stringify(baseData));

    // 階層構造構築
    buildHierarchyProcess(result);

    for (const additional of additionalData) {
      const baseProcess = result.find(b => b.uniqueProcessId === additional.uniqueProcessId && b.uniqueLevel === additional.uniqueLevel);

      if (baseProcess) {
        // 既存データあり
        baseProcess.uniqueProcessCycleTimeInSecondByPartNo =
          mergePartNo(baseProcess.uniqueProcessCycleTimeInSecondByPartNo ?? "",
            additional.uniqueProcessCycleTimeInSecondByPartNo ?? "");
      } else {
        // 既存データなし

        // additionalの親プロセスが誰なのか
        const tempParentProcess = getParentProcess(additionalData, additional);

        // result(baseData)から親プロセスを探す
        const parentProcess = result.find(p => p.uniqueProcessId === tempParentProcess?.uniqueProcessId);

        if (parentProcess?.subProcess) {
          // result(baseData)の親プロセスの子プロセスを探す

          // 取り込み対象のSeqより小さいものを探す。
          const thresholdProcesses = parentProcess.subProcess?.filter(p => p.uniqueProcessSeq < additional.uniqueProcessSeq);

          let thresholdProcess: UniqueProcessType | null = null;
          if (thresholdProcesses.length > 0) {
            // 探したものの中から、一番近いもの（末尾のもの）を取得（見つからなかった場合は先頭が対象）
            thresholdProcess = thresholdProcesses[thresholdProcesses.length - 1];

            const addIndex = parentProcess.subProcess.indexOf(thresholdProcess);

            // thresholdProcess の後に追加
            parentProcess.subProcess.splice(addIndex + 1, 0, additional);
          } else {
            thresholdProcess = parentProcess.subProcess[0];

            // thresholdProcess の前に追加
            const addIndex = 0;
            parentProcess.subProcess.splice(addIndex, 0, additional);
          }

          // 親の位置
          const parentIndex = result.indexOf(parentProcess);

          // 子の位置
          const additionalIndex = parentProcess.subProcess.indexOf(additional);

          // 親の位置から進める孫の数
          const subCount = getSubProcessCount(parentProcess.subProcess?.filter((_, idx) => idx <= additionalIndex));

          // 全体の追加位置算出
          const resultAddIndex = parentIndex + (additionalIndex + 1) + subCount;

          // additionalのseqの次の子プロセスがない場合は、子プロセスの最後に追加する。
          result.splice(resultAddIndex, 0, additional);
        } else {
          // 子プロセスがない場合は、親プロセスの次に追加する。
          const parentIndex = result.findIndex(p => p.uniqueProcessId === parentProcess?.uniqueProcessId);
          result.splice(parentIndex + 1, 0, additional);

          if (parentProcess) {
            parentProcess.subProcess = [];
            parentProcess.subProcess.push(additional);
          }
        }
      }
    }

    const minResultSeq = result.reduce((min, item) => Math.min(min, item.uniqueProcessSeq), Infinity);

    // 不要な情報を削除
    result = result.map((p, idx) => ({
      uniqueProcessId: p.uniqueProcessId,
      uniqueProcessName: p.uniqueProcessName,
      uniqueProcessSeq: minResultSeq + idx,  // seqは重複のない値に振り直す
      uniqueLevel: p.uniqueLevel,
      uniqueProcessCycleTimeInSecondByPartNo: p.uniqueProcessCycleTimeInSecondByPartNo,
    } as UniqueProcessType));

    return result;
  }

  /**
   * 各項目の位置を取得する。
   * @param header ヘッダ部
   */
  const getCellPositions = (header: string[]) => {
    console.log(`header`, header);
    const cellPositions = {
      seq: -1,
      level: -1,
      modelNo: -1,
      processCode: -1,
      itemNo: -1,
      itemName: -1,
      usage: -1,
      unitPrice: -1,
      amount: -1,
      unitTime: -1,
    };

    header.forEach((h: string, i: number) => {
      switch (h) {
        case "Seq.":
          cellPositions.seq = i;
          break;
        case "Level":
          cellPositions.level = i;
          break;
        case "Model No.":
          cellPositions.modelNo = i;
          break;
        case "Process Code":
          cellPositions.processCode = i;
          break;
        case "Item No.":
          cellPositions.itemNo = i;
          break;
        case "Item Name":
          cellPositions.itemName = i;
          break;
        case "Usage":
          cellPositions.usage = i;
          break;
        case "U/P":
          cellPositions.unitPrice = i;
          break;
        case "Amount":
          cellPositions.amount = i;
          break;
        case "Unit Time":
          cellPositions.unitTime = i;
          break;
      }
    });

    // すべての項目位置を取得できたかをチェック
    const result = Object.entries(cellPositions).every(([key, value]) => value >= 0);

    if (!result) {
      throw new Error(`Could not determine the item position from the header section.`);
    }
    return cellPositions;
  }

  /**
   * プロセスコードがない場合は、ダミーコードを採番する。
   * @param cbo CBOMレコード
   * @returns ダミーのプロセスコード
   */
  const generateProcessCode = (cbom: CBomType) => {
    // DUMMY-[Model No.]-[Item No.]-[Level]
    return `DUMMY-${(cbom.modelNo)?.trim()}-${(cbom.itemNo)?.trim()}-${(cbom.level)?.trim()}`;
  }

  /**
   * プロセス情報を２次元配列に変換する（スプレッドシート反映用）
   * @param uniqueProcessData プロセス情報
   * @param partNo パーツNo
   * @returns
   */
  const convertArray = (uniqueProcessData: UniqueProcessType[], partNo: string): string[][] => {
    const rows: string[][] = [];

    for (const p of uniqueProcessData) {
      const columns: string[] = [];

      if (!p.uniqueProcessCycleTimeInSecondByPartNoList && p.uniqueProcessCycleTimeInSecondByPartNo) {
        const obj = JSON.parse(p.uniqueProcessCycleTimeInSecondByPartNo);
        p.uniqueProcessCycleTimeInSecondByPartNoList = obj;
      }

      // パーツNo検索
      const partNoCycleTime = p.uniqueProcessCycleTimeInSecondByPartNoList?.find(t => t.partNo === partNo);

      if (!partNoCycleTime) {
        // パーツNoがない場合は、対象外
        continue;
      }

      // Seq.
      columns[0] = String(p.uniqueProcessSeq ?? "");
      // Level
      columns[1] = String(p.uniqueLevel ?? "");
      // Model No.
      columns[2] = "";
      // Process Code
      columns[3] = p.uniqueProcessId ?? "";
      // Item No.
      columns[4] = p.uniqueProcessName ?? "";
      // Item Name
      columns[5] = partNoCycleTime.itemName ?? "";
      // Usage
      columns[6] = "";
      // U/P
      columns[7] = "";
      // Amount
      columns[8] = "";
      // Unit Time
      columns[9] = partNoCycleTime.cycleTime;

      rows.push(columns);
    }

    return rows;
  }

  /**
   * プロセスフロー情報登録
   * @param bomSheetsData 全シートデータ
   * @returns true: 成功, false: 失敗
   */
  const saveProcessData = async (bomSheetsData: BomSheetsData, firstYearMonth: string): Promise<boolean> => {
    const functionName = "saveProcessData";

    try {
      // CycletimeシートのcycleTimeが入力されている行のみ抽出
      const filteredData = bomSheetsData.cycletime.dataRows.filter(
        row => Object.values(row.monthlyData).some(cycleTime => cycleTime !== "") && (row.seq ?? "") !== ""
      );

      // CycletimeRow[] → UniqueProcessType[] 変換
      const inputProcessData: UniqueProcessType[] = filteredData.map((row) => {
        // levelの変換: "-" → 0, "*01" → 1, "**02" → 2, etc.
        const levelStr = row.level ?? "";
        let levelNum = 0;
        if (levelStr === "-") {
          levelNum = 0;
        } else {
          // "*01" → 1, "**02" → 2 のようにアスタリスクの数を数える
          const asteriskMatch = levelStr.match(/^(\*+)/);
          if (asteriskMatch) {
            levelNum = asteriskMatch[1].length;
          } else {
            // 数値の場合はそのまま使用
            const num = Number(levelStr);
            if (!isNaN(num)) {
              levelNum = num;
            }
          }
        }

        const processInfo: UniqueProcessType = {
          uniqueProcessId: row.processCode || null,
          uniqueProcessName: row.itemNo || null,
          uniqueProcessSeq: Number(row.seq) || 0,
          uniqueLevel: levelNum,
          uniqueProcessCycleTimeInSecondByPartNo: JSON.stringify([{
            partNo: activePartNo,
            cycleTime: firstYearMonth ? row.monthlyData[firstYearMonth] : null,  // CycletimeシートのcycleTimeは秒単位
            cycleTimeMonthly: row.monthlyData,
            itemName: row.itemName,
          }]),
          posX: 0,
          posY: 0,
        };
        return processInfo;
      });

      // 当該パーツNoのプロセスを削除(既存データがある場合を考慮)
      let tempUniqueProcessData = removeProcess(uniqueProcessData, activePartNo!);

      // 念のため、ソート
      inputProcessData.sort((a, b) => a.uniqueProcessSeq - b.uniqueProcessSeq);
      tempUniqueProcessData.sort((a, b) => a.uniqueProcessSeq - b.uniqueProcessSeq);

      // マージ
      const mergedData = mergeUniqueProcess(tempUniqueProcessData, inputProcessData, activePartNo!, projectId);

      setUniqueProcessDataWithCopy(mergedData);

      // プロセスフロー描画
      drawProcessFlowWithParamData(mergedData);

      // 編集したパーツNoを登録・更新用に保持
      changeEditPartNo(activePartNo!);

      // DB登録
      const putUniqueProcessResult = await putUniqueProcess(mergedData);

      if (putUniqueProcessResult !== "success") {
        console.error(`${functionName} プロセスフロー情報の更新に失敗しました。`, putUniqueProcessResult);
        return false;
      }

      // プロジェクト/PartNo/プロセス紐づけ情報 登録・更新（DEL&INS）
      const putPartNoAndProcessResult = await putPartNoAndProcess(projectId, partNoList.map(p => p.partNo), mergedData);

      if (putPartNoAndProcessResult !== "success") {
        console.error(`${functionName} プロジェクト/PartNo/プロセス紐づけ情報の保存に失敗しました。`, putPartNoAndProcessResult);
        return false;
      }

      return true;

    } catch (error) {
      console.error(`${functionName} エラーが発生しました。`, error);
      return false;
    }
  }

  /**
   * 材料費情報登録
   * @param bomSheetsData 全シートデータ
   * @returns true: 成功, false: 失敗
   */
  const saveMaterialCostData = async (bomSheetsData: BomSheetsData): Promise<boolean> => {
    const functionName = "saveMaterialCostData";

    try {
      // MaterialCostシートのデータを取得(材料費のみに絞り込み)
      const materialCostRows = bomSheetsData.materialCost.dataRows
        .filter((_, idx) => bomSheetsData.processBom.dataRows[idx]?.isMaterialCostRow);

      // 年月リストを生成
      const startDate = dayjs(projectWithRdsData?.projectStartDate);
      const endDate = dayjs(projectWithRdsData?.projectEndDate);

      const yearMonthList: string[] = [];

      if (startDate.isValid() && endDate.isValid()) {
        let current = startDate.startOf("month");
        const endMonth = endDate.startOf("month");

        while (current.isBefore(endMonth) || current.isSame(endMonth)) {
          yearMonthList.push(current.format("YYYY-MM"));
          current = current.add(1, "month");
        }
      }

      // ローカル通貨名を取得
      const localCurrencyName = currencyData.find(c => c.currencyId === localCurrencyId)?.currencyName || "";

      /**
       * 通貨IDから通貨名を取得するヘルパー関数
       * @param currencyId 通貨ID
       * @returns 通貨名（見つからない場合は空文字列）
       */
      const getCurrencyName = (currencyId: string | null | undefined): string => {
        if (!currencyId) return "";
        return currencyData.find(c => c.currencyId === currencyId)?.currencyName || "";
      };

      /**
       * 購入通貨からローカル通貨に変換するヘルパー関数
       * 振り返りモードでは月別レート、通常モードでは申請時レート（単一）で変換
       * @param amount 金額
       * @param sourceCurrencyName 購入通貨名
       * @param yearMonth 対象年月（振り返りモード時の月別レート検索用、"YYYY-MM"）
       * @returns 変換後の金額
       */
      const convertToLocalCurrency = (amount: number, sourceCurrencyName: string, yearMonth?: string): number => {
        if (amount === 0 || !sourceCurrencyName || !localCurrencyName) return amount;
        if (sourceCurrencyName === localCurrencyName) return amount;
        // 振り返りモード: 月別レートで変換（各月ごとに異なるレート）
        if (reviewDate && monthlyRateMap && yearMonth) {
          return convertCurrencyByMonth(amount, sourceCurrencyName, localCurrencyName, yearMonth, monthlyRateMap, currencyData).toNumber();
        }
        // 通常モード: 申請時レート（単一レート）で変換
        return convertCurrency(amount, sourceCurrencyName, localCurrencyName, currencyData).toNumber();
      };

      /**
       * 年月別データから指定年月の値を取得するヘルパー関数
       * @param monthlyData 年月別データ
       * @param yearMonth 対象年月
       * @returns 数値（存在しない場合は0）
       */
      const getMonthlyValue = (monthlyData: Record<string, string> | undefined, yearMonth: string): number => {
        if (!monthlyData || !yearMonth) return 0;
        return Number(monthlyData[yearMonth]) || 0;
      };

      /**
       * 指定年月の材料費計算を行うヘルパー関数
       * @param row MaterialCostRow
       * @param yearMonth 対象年月
       * @param purchaseCurrencyName 購入通貨名
       * @returns 計算結果オブジェクト
       */
      const calcMaterialCostForMonth = (row: typeof materialCostRows[0], yearMonth: string, purchaseCurrencyName: string) => {
        const usage = Number(row.usage) || 0;
        const unitPriceCombined = getMonthlyValue(row.unitPriceCombinedMonthly, yearMonth);
        const unitPriceInhouse = getMonthlyValue(row.unitPriceInhouseMonthly, yearMonth);
        const unitPriceExternal = getMonthlyValue(row.unitPriceExternalMonthly, yearMonth);
        const inhouseRatio = getMonthlyValue(row.inhouseRatioMonthly, yearMonth);
        const materialYield = getMonthlyValue(row.materialYieldMonthly, yearMonth);
        const amount = getMonthlyValue(row.amountMonthly, yearMonth);

        // 有効な歩留まり (materialYield) の値を確認
        const validYield = new Decimal(materialYield).greaterThan(0) ? new Decimal(materialYield) : new Decimal(1);

        // 有効な内製比率 (inhouseRatio) の値を確認 (0～100%)
        const validInhouseRatio = new Decimal(inhouseRatio).greaterThanOrEqualTo(0) && new Decimal(inhouseRatio).lessThanOrEqualTo(100)
          ? new Decimal(inhouseRatio)
          : new Decimal(0);

        // inhouseUnitPrice が 0 の場合は outsourcingUnitPrice を使用
        const effectiveInhouseUnitPrice = new Decimal(unitPriceInhouse).greaterThan(0)
          ? new Decimal(unitPriceInhouse)
          : new Decimal(unitPriceExternal);

        // 内製数量 & 外注数量の計算
        const inhouseQuantity = new Decimal(usage).times(validInhouseRatio).dividedBy(100);
        const outsourcingQuantity = new Decimal(usage).minus(inhouseQuantity);

        // 金額計算
        const inhouseMaterialCost = inhouseQuantity.times(effectiveInhouseUnitPrice);
        const outsourcingMaterialCost = outsourcingQuantity.times(new Decimal(unitPriceExternal));
        const totalMaterialCost = inhouseMaterialCost.plus(outsourcingMaterialCost);

        // 内製比率、歩留まりを考慮した原価(InternalAdjustedAmount) の計算
        const internalAdjustedAmount = roundToNineDecimalPlaces(totalMaterialCost.dividedBy(validYield).times(100).toNumber());

        // 外注原価計算 (歩留まり含む)
        const outsourcingAmount = validYield.toNumber() > 0
          ? roundToNineDecimalPlaces(new Decimal(usage).times(new Decimal(unitPriceExternal)).dividedBy(validYield).times(100).toNumber())
          : 0;

        // 内製原価計算 (歩留まり含む)
        const inhouseAmount = validYield.toNumber() > 0
          ? roundToNineDecimalPlaces(new Decimal(usage).times(effectiveInhouseUnitPrice).dividedBy(validYield).times(100).toNumber())
          : 0;

        // 購入通貨からローカル通貨に変換（振り返りモードでは月別レート、通常モードでは申請時レート）
        return {
          Usage: usage,
          UnitPrice: convertToLocalCurrency(unitPriceCombined, purchaseCurrencyName, yearMonth),
          Amount: convertToLocalCurrency(amount, purchaseCurrencyName, yearMonth),
          InternalAdjustedAmount: convertToLocalCurrency(internalAdjustedAmount, purchaseCurrencyName, yearMonth),
          outsourcingUnitPrice: convertToLocalCurrency(unitPriceExternal, purchaseCurrencyName, yearMonth),
          inhouseUnitPrice: convertToLocalCurrency(unitPriceInhouse, purchaseCurrencyName, yearMonth),
          outsourcingAmount: convertToLocalCurrency(outsourcingAmount, purchaseCurrencyName, yearMonth),
          inhouseAmount: convertToLocalCurrency(inhouseAmount, purchaseCurrencyName, yearMonth),
          inhouseRatio: inhouseRatio,
          yield: materialYield,
        };
      };

      // 空データを除外してCBomData形式に変換（年月別データを含む）
      const cbomDataList = materialCostRows
        .filter(row => row.seq && row.seq !== "")
        .map(row => {
          // 購入通貨名を取得
          const purchaseCurrencyName = getCurrencyName(row.purchaseCurrencyId);

          // 年月別計算結果を格納するオブジェクト
          const monthlyCalcResults: Record<string, ReturnType<typeof calcMaterialCostForMonth>> = {};
          for (const yearMonth of yearMonthList) {
            monthlyCalcResults[yearMonth] = calcMaterialCostForMonth(row, yearMonth, purchaseCurrencyName);
          }

          // 初年度の値（後方互換性のため）
          const firstYearMonth = yearMonthList.length > 0 ? yearMonthList[0] : "";
          const firstMonthCalc = firstYearMonth ? monthlyCalcResults[firstYearMonth] : calcMaterialCostForMonth(row, "", purchaseCurrencyName);

          return {
            seq: row.seq,
            ItemNo: row.itemNo,
            ItemName: row.itemName,
            // 初年度の値（後方互換性）
            Usage: firstMonthCalc.Usage,
            UnitPrice: firstMonthCalc.UnitPrice,
            Amount: firstMonthCalc.Amount,
            InternalAdjustedAmount: firstMonthCalc.InternalAdjustedAmount,
            outsourcingUnitPrice: firstMonthCalc.outsourcingUnitPrice,
            inhouseUnitPrice: firstMonthCalc.inhouseUnitPrice,
            outsourcingAmount: firstMonthCalc.outsourcingAmount,
            inhouseAmount: firstMonthCalc.inhouseAmount,
            inhouseRatio: firstMonthCalc.inhouseRatio,
            yield: firstMonthCalc.yield,
            // 年月別計算結果
            monthlyData: monthlyCalcResults,
          };
        });

      // パーツNo別の更新日時
      const partNoUpdateDate = partNoList.find((p: any) => p.partNo === activePartNo)?.updateDate || "";

      // materialCostObject: partNoをキーとしてcbomRawDataを格納
      const materialCostObject = {
        [activePartNo!]: cbomDataList,
      };

      // materialCostWithInhouseProfit: partNoとcbomDataを格納
      const materialCostWithInhouseProfit = {
        partNo: activePartNo,
        cbomData: cbomDataList,
      };

      // 内製比率情報を構築（内製が有効な場合は1、それ以外は0）
      const partNoInhouseStatus = {
        partNo: activePartNo,
        inhousePart: cbomDataList.some((row) => {
          // 条件: inhouseUnitPrice が 0 以外かつ inhouseRatio が 0 以外
          return row.inhouseUnitPrice !== 0 && row.inhouseRatio !== 0;
        })
          ? 1  // 条件を満たす場合は 1
          : 0, // 条件を満たさない場合は 0
      };

      const input = {
        projectId: projectId,
        materialCost: JSON.stringify(materialCostObject),
        materialCostWithInhouseProfit: JSON.stringify([materialCostWithInhouseProfit]),
        inhousePart: JSON.stringify([partNoInhouseStatus]),
        partNoUpdateDate: JSON.stringify({ [activePartNo!]: partNoUpdateDate }),
        ...(reviewDate && { reviewDate }),
      };

      const query = `
        mutation updateMaterial ($input: MaterialInput!) {
          updateMaterial(input: $input) { status }
        }
      `;
      const result = await WebAPI.request({
        query: query,
        variables: { input },
      });

      if (result?.data?.updateMaterial?.status === "success") {
        console.log(`${functionName}: PartNo "${activePartNo}" を更新しました。`);
        return true;
      } else if (result?.data?.updateMaterial?.status === "expired") {
        console.error("${functionName}: 他のユーザーが更新したため処理が失敗しました。");
        return false;
      } else {
        console.error("${functionName}: 更新に失敗しました。", result);
        return false;
      }
    } catch (error) {
      console.error("${functionName}: Error", error);
      return false;
    }
  }

  /**
   * 最大サイクルタイムと歩留まり算出
   * @param cycleTimeMonthlyDataList 
   * @param yieldMonthlyDataList 
   * @param firstYearMonth 初年度年月
   * @returns 
   */
  const calcCycleTimeAndYield = (
    cycleTimeMonthlyDataList: Record<string, string>[], yieldMonthlyDataList: Record<string, string>[], firstYearMonth: string) => {

    const functionName = "calcCycleTimeAndYield";

    try {
      // 最大サイクルタイム;
      let maxCycleTime = 0;
      for (const monthlyData of cycleTimeMonthlyDataList) {
        // 各要素に対して再帰的に最大値を取得
        const cycleTime = parseFloat(monthlyData[firstYearMonth]);
        if (maxCycleTime < cycleTime) {
          maxCycleTime = cycleTime;
        }
      }

      // 歩留まり
      let hasValue = false;
      let yieldMultiplyValue: Decimal | null = yieldMonthlyDataList.reduce((acc: Decimal, monthlyData: Record<string, string>) => {
        let tempValue = monthlyData[firstYearMonth] || null;

        if (!tempValue) {
          // 空白は計算対象外
          return acc;
        }

        hasValue = true;

        let value;
        value = new Decimal(tempValue);
        acc = acc.mul(value.div(100));

        return acc;
      }, new Decimal(1));

      if (!hasValue) {
        yieldMultiplyValue = null;
      }

      return {
        maxCycleTime: maxCycleTime,
        yieldRate: (yieldMultiplyValue?.mul(100)?.toFixed(2)) || null,
      };
    } catch (error) {
      console.error(`${functionName}: Error`, error);
      return {
        maxCycleTime: null,
        yieldRate: null,
      };
    }
  }

  /**
   * 浮動小数点以下(9桁)の丸め処理
   * @param num 数値
   * @returns 丸め後の数値
   */
  const roundToNineDecimalPlaces = (num: number): number => {
    return Math.round(num * 1e9) / 1e9;
  }

  /**
   * レイアウト
   */
  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
    >
      <HStack align="center">
        <Button colorScheme="blue" isLoading={isLoading} onClick={() => {
          if (!projectId) {
            toast({
              title: TITLE,
              description: t('features.InvestmentScreening.ProductionProcess.message.registerFromBasicInfo'),
              status: 'warning',
              duration: 5000,
              isClosable: true,
            });
            return;
          }
          onOpenSelectPartModal();
        }}>
          {t('features.InvestmentScreening.ProductionProcess.button.bomEdit')}
        </Button>
        <Button
          colorScheme="teal"
          onClick={onOpenBomCreateModal}
          isDisabled={!pagePermissions?.isEditable}
        >
          {t('features.InvestmentScreening.ProductionProcess.button.bomCreate')}
        </Button>
        <HelpIcon width="auto">
          {t('features.InvestmentScreening.ProductionProcess.help.processCreate')}
        </HelpIcon>
      </HStack>
      <HStack align="start" mt={2}>
        <FormLabel mt={2} mr={1} style={{ whiteSpace: "nowrap", fontWeight: "bold" }}>{t('features.InvestmentScreening.ProductionProcess.field.productionLineName')}</FormLabel>
        <Input width="500px" maxLength={100} value={productionLine?.productionLineName} isDisabled={!pagePermissions?.isEditable}
          onChange={(event) => { changeProductionLine("productionLineName", event.target.value); }} />

        <FormControl isInvalid={productLineErrors.workingDays.isError} w="auto">
          <Button colorScheme="gray" border={productLineErrors.workingDays.isError ? "solid 2px red" : hasDraftWorkingDays ? "solid 2px orange" : ""}
            isDisabled={!isRequiredDataValid}
            onClick={(event) => {
              // フォーカスを外す
              event.currentTarget.blur();

              // 本データ ⇒ 一時保持データにコピー
              if (Array.isArray(workingDaysData) && workingDaysData.length > 0) {
                // 本データが登録されている場合のみコピー
                setTempWorkingDaysData(workingDaysData);
              }

              // 稼働日数モーダル表示
              onOpenWorkingDaysModal();
            }}>{t('features.InvestmentScreening.ProductionProcess.field.workingDays')}</Button>
          <FormErrorMessage>{productLineErrors.workingDays.errorMessage}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={productLineErrors.nonWorkingHours.isError} w="auto">
          <Button colorScheme="gray" border={productLineErrors.nonWorkingHours.isError ? "solid 2px red" : hasDraftNonWorkingHours ? "solid 2px orange" : ""}
            isDisabled={!isRequiredDataValid}
            onClick={(event) => {
              // フォーカスを外す
              event.currentTarget.blur();

              // 本データ ⇒ 一時保持データにコピー
              if (Array.isArray(nonWorkingHoursData) && nonWorkingHoursData.length > 0) {
                // 本データが登録されている場合のみコピー
                setTempNonWorkingHoursData(nonWorkingHoursData);
              }

              // 非稼働時間モーダル表示
              onOpenNonWorkingHoursModal();
            }}>{t('features.InvestmentScreening.ProductionProcess.field.nonWorkingHours')}</Button>
          <FormErrorMessage>{productLineErrors.nonWorkingHours.errorMessage}</FormErrorMessage>
        </FormControl>

        {/* 基準月設定ボタン（新製品プロジェクトのときのみ表示） */}
        {projectWithRdsData?.purpose === PurposeType.NewProduct.code && (
          <Button colorScheme="gray" whiteSpace="nowrap" flexShrink={0}
            border={hasDraftBaseMonth ? "solid 2px orange" : ""}
            isDisabled={!isRequiredDataValid}
            onClick={(event) => {
              event.currentTarget.blur();
              setTempBaseMonthYield(productionLine.improvementBaseMonthYield ?? null);
              setTempBaseMonthAvailability(productionLine.improvementBaseMonthAvailability ?? null);
              setTempBaseMonthCycleTime(productionLine.improvementBaseMonthCycleTime ?? null);
              onOpenBaseMonthModal();
            }}>{t('features.InvestmentScreening.ProductionProcess.field.baseMonthSetting')}</Button>
        )}

        {/* 一時保存データがある場合の案内メッセージ */}
        {(hasDraftWorkingDays || hasDraftNonWorkingHours || hasDraftBaseMonth) && (
          <Text fontSize="sm" color="orange.600" whiteSpace="nowrap" ml={2} alignSelf="center">{t('features.InvestmentScreening.ProductionProcess.help.draftNotice')}</Text>
        )}
      </HStack>

      <Box height="calc(100vh - 335px)" mt={2} overflow="auto" border="1px solid #ccc">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodesDraggable={false}
          nodeTypes={nodeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          minZoom={0.01}
          nodesConnectable={false}
          selectionOnDrag={false}
          fitView
        />
      </Box>

      {/* 確認ボタン */}
      <Flex justify="flex-end" mt="20px">
        <Box>
          <Button
            colorScheme="blue"
            onClick={onOpenFileUploadModal}
            isDisabled={!pagePermissions?.isEditable}
          >
            {t('features.InvestmentScreening.ProductionProcess.action.lineImageUpload')}
          </Button>
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="companyDivision"
            onClick={handleModalOpen}
            isDisabled={!pagePermissions?.eabledCompanyDivision}
            ref={refs["companyDivision"]}
          >
            {t('features.InvestmentScreening.ProductionProcess.action.companyDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
        </Box>
        <Box>
          <Button
            ml="10px"
            colorScheme="blue"
            name="procurementDivision"
            onClick={handleModalOpen}
            isDisabled={!pagePermissions?.eabledProcurementDivision}
            ref={refs["procurementDivision"]}
          >
            {t('features.InvestmentScreening.ProductionProcess.action.procurementDivisionConfirm')}
          </Button>
          <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
        </Box>
      </Flex>

      {/* 稼働日数モーダル */}
      <Modal isOpen={isOpenWorkingDaysModal} onClose={() => { onCloseWorkingDaysModal() }} initialFocusRef={workingDaysInitialFocusRef} closeOnOverlayClick={false} onCloseComplete={handleCloseCompleteInputModal}>
        <ModalOverlay />
        <ModalContent minW={1300} maxH="85dvh">
          <ModalHeader>
            <HStack spacing={4}>
              <Text whiteSpace="nowrap">{t('features.InvestmentScreening.ProductionProcess.field.workingDays')}</Text>
              <HelpIcon placement="bottom-start">
                <Text>
                  {t('features.InvestmentScreening.ProductionProcess.help.workingDays')}
                </Text>
              </HelpIcon>
              <HStack spacing={4}>
                <ButtonGroup spacing={4} isAttached>
                  <Button colorScheme={inputMode === "manual" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                    setInputMode("manual");
                    handleMaualClick();
                  }}>
                    {t('features.InvestmentScreening.ProductionProcess.button.inputOnScreen')}
                  </Button>

                  <Button colorScheme={inputMode === "google" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                    setInputMode("google");
                    handleGoogleSpreadClickByworkingDays("workingDays");
                  }}>
                    GoogleSpread
                  </Button>
                </ButtonGroup>
              </HStack>
              <Button colorScheme="blue" onClick={() => handleSyncDataByWorkingDays("workingDays")} disabled={!pagePermissions?.isEditable || inputMode === "manual"} isLoading={isLoading}>
                {t('features.InvestmentScreening.ProductionProcess.button.dataSync')}
              </Button>
            </HStack>
            {/* 無効値エラーメッセージ */}
            {workingDaysSyncError.invalidValueErrors.length > 0 && (
              <Box mt={2}>
                {workingDaysSyncError.invalidValueErrors.map((error, index) => (
                  <Text key={index} color="orange.500" fontSize="sm">{error}</Text>
                ))}
              </Box>
            )}
          </ModalHeader>
          <ModalBody overflowY="auto">
            {/* 読み取り専用時は入力不可 */}
            <fieldset disabled={!pagePermissions?.isEditable} style={{ minWidth: 0 }}>
              <WorkingDays
                showWorkOnHoliday={true}
                workingDays={tempWorkingDaysData}
                setWorkingDays={setTempWorkingDaysData}
                setIsValidWorkingDays={setIsValidWorkingDays}
                projectStartDate={projectWithRdsData?.projectStartDate || dayjs().format("YYYY-MM-DD")}
                maxHeight=""
              />
            </fieldset>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Box>
              {hasDraftWorkingDays && (
                <Button colorScheme="red" isDisabled={!pagePermissions?.isEditable} onClick={async (event) => {
                  event.currentTarget.blur();
                  await discardDraft("workingDays");
                  onCloseWorkingDaysModal();
                }}>{t('features.InvestmentScreening.ProductionProcess.button.discardDraft')}</Button>
              )}
            </Box>
            <Box display="flex">
              <Button colorScheme="blue" m={1} isDisabled={!pagePermissions?.isEditable || !isValidWorkingDays} onClick={(event) => {
                // フォーカスを外す
                event.currentTarget.blur();

                // 一時保存用から反映
                setWorkingDaysData(tempWorkingDaysData);

                // DBに一時保存
                saveDraft("workingDays", JSON.stringify(tempWorkingDaysData));
                setHasDraftWorkingDays(true);

                onCloseWorkingDaysModal();
              }}>{t('common.button.confirmAction')}</Button>
              <Button ref={workingDaysInitialFocusRef} m={1} onClick={(event) => {
                // フォーカスを外す
                event.currentTarget.blur();
                onCloseWorkingDaysModal();
              }}>{t('common.button.cancel')}</Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 非稼働時間モーダル */}
      <Modal isOpen={isOpenNonWorkingHoursModal} onClose={() => { onCloseNonWorkingHoursModal() }} closeOnOverlayClick={false} onCloseComplete={handleCloseCompleteInputModal}>
        <ModalOverlay />
        <ModalContent minW={1000} maxH="85dvh">
          <ModalHeader>
            <HStack spacing={4}>
              <Text whiteSpace="nowrap">{t('features.InvestmentScreening.ProductionProcess.field.nonWorkingHours')}</Text>
              <HelpIcon placement="bottom-start">
                <Text whiteSpace="pre-wrap">{t('features.InvestmentScreening.ProductionProcess.help.nonWorkingHours')}</Text>
              </HelpIcon>
              <HStack spacing={4}>
                <ButtonGroup spacing={4} isAttached>
                  <Button colorScheme={inputMode === "manual" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                    setInputMode("manual");
                    handleMaualClick();
                  }}>
                    {t('features.InvestmentScreening.ProductionProcess.button.inputOnScreen')}
                  </Button>

                  <Button colorScheme={inputMode === "google" ? "green" : "gray"} isLoading={isLoading} onClick={() => {
                    setInputMode("google");
                    handleGoogleSpreadClickByworkingDays("nonWorkingDays");
                  }}>
                    GoogleSpread
                  </Button>
                </ButtonGroup>
              </HStack>
              <Button colorScheme="blue" onClick={() => handleSyncDataByWorkingDays("nonWorkingDays")} disabled={!pagePermissions?.isEditable || inputMode === "manual"} isLoading={isLoading}>
                {t('features.InvestmentScreening.ProductionProcess.button.dataSync')}
              </Button>
            </HStack>
            {/* バリデーションエラーメッセージ */}
            {nonWorkingHoursSyncError.workingHoursErrors.length > 0 && (
              <Box mt={2}>
                {nonWorkingHoursSyncError.workingHoursErrors.map((error, index) => (
                  <Text key={index} color="red.500" fontSize="sm">{error}</Text>
                ))}
              </Box>
            )}
            {nonWorkingHoursSyncError.nonWorkingHourErrors.length > 0 && (
              <Box mt={2}>
                {nonWorkingHoursSyncError.nonWorkingHourErrors.map((error, index) => (
                  <Text key={index} color="red.500" fontSize="sm">{error}</Text>
                ))}
              </Box>
            )}
            {/* 無効値エラーメッセージ */}
            {nonWorkingHoursSyncError.invalidValueErrors.length > 0 && (
              <Box mt={2}>
                {nonWorkingHoursSyncError.invalidValueErrors.map((error, index) => (
                  <Text key={index} color="orange.500" fontSize="sm">{error}</Text>
                ))}
              </Box>
            )}
          </ModalHeader>
          <ModalBody overflowY="auto">
            {/* 読み取り専用時は入力不可 */}
            <fieldset disabled={!pagePermissions?.isEditable} style={{ minWidth: 0 }}>
              <NonWorkingHours
                isSimulation={false}
                nonWorkingHoursData={tempNonWorkingHoursData}
                setNonWorkingHoursData={setTempNonWorkingHoursData}
                projectStartDate={projectWithRdsData?.projectStartDate || dayjs().format("YYYY-MM-DD")}
                onValueChange={handleNonWorkingHoursValueChange}
                maxHeight=""
              />
            </fieldset>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Box>
              {hasDraftNonWorkingHours && (
                <Button colorScheme="red" isDisabled={!pagePermissions?.isEditable} onClick={async (event) => {
                  event.currentTarget.blur();
                  await discardDraft("nonWorkingHours");
                  onCloseNonWorkingHoursModal();
                }}>{t('features.InvestmentScreening.ProductionProcess.button.discardDraft')}</Button>
              )}
            </Box>
            <Box display="flex">
              <Button colorScheme="blue" m={1} isDisabled={!pagePermissions?.isEditable} onClick={(event) => {
                // フォーカスを外す
                event.currentTarget.blur();

                // エラーメッセージ（赤色）が存在する場合はトーストを表示して閉じない
                if (nonWorkingHoursSyncError.workingHoursErrors.length > 0 ||
                  nonWorkingHoursSyncError.nonWorkingHourErrors.length > 0
                ) {
                  toast({
                    title: t('features.InvestmentScreening.ProductionProcess.field.nonWorkingHours'),
                    description: t('features.InvestmentScreening.ProductionProcess.message.inputError'),
                    status: 'warning',
                    duration: 2000,
                    isClosable: true,
                  });
                  return;
                }

                // 一時保存用から反映
                setNonWorkingHoursData(tempNonWorkingHoursData);
                console.log(tempNonWorkingHoursData);

                // 残業有無データを稼働日数データに反映（残業時間欄に値が入っているかどうかから算出する）
                if (tempNonWorkingHoursData.length > 0) {
                  setWorkingDaysData((prev) =>
                    prev.map((wd) => {
                      const matchingNonWorkingHours = tempNonWorkingHoursData.find(
                        (item) => isSameFiscalYear(item.fiscalYear, wd.fiscalYear)
                      );
                      if (matchingNonWorkingHours) {
                        return {
                          ...wd,
                          workingDaysData: {
                            ...wd.workingDaysData,
                            requiredOverTimes: matchingNonWorkingHours.nonWorkingHoursByMonth.map(
                              (monthData) => !!monthData.overTime && monthData.overTime !== "00:00"
                            ),
                          },
                        };
                      }
                      return wd;
                    })
                  );
                }

                // DBに一時保存
                saveDraft("nonWorkingHours", JSON.stringify(tempNonWorkingHoursData));
                setHasDraftNonWorkingHours(true);

                onCloseNonWorkingHoursModal();
              }}>{t('common.button.confirmAction')}</Button>
              <Button m={1} onClick={(event) => {
                // フォーカスを外す
                event.currentTarget.blur();
                onCloseNonWorkingHoursModal();
              }}>{t('common.button.cancel')}</Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 基準月設定モーダル */}
      <Modal isOpen={isOpenBaseMonthModal} onClose={() => { onCloseBaseMonthModal() }} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent minW={560}>
          <ModalHeader>{t('features.InvestmentScreening.ProductionProcess.modal.baseMonthSetting')}</ModalHeader>
          <ModalBody>
            <Text mb={4} fontSize="sm" whiteSpace="pre-wrap">{t('features.InvestmentScreening.ProductionProcess.help.baseMonthDescription')}</Text>
            <FormControl mb={3}>
              <FormLabel fontSize="sm" mb={1}>{t('features.InvestmentScreening.ProductionProcess.field.yield')}</FormLabel>
              <Input type="month" size="sm" value={tempBaseMonthYield ?? ""} onChange={(e) => setTempBaseMonthYield(e.target.value || null)} />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm" mb={1}>{t('features.InvestmentScreening.ProductionProcess.field.availabilityRate')}</FormLabel>
              <Input type="month" size="sm" value={tempBaseMonthAvailability ?? ""} onChange={(e) => setTempBaseMonthAvailability(e.target.value || null)} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>{t('features.InvestmentScreening.ProductionProcess.field.cycleTime')}</FormLabel>
              <Input type="month" size="sm" value={tempBaseMonthCycleTime ?? ""} onChange={(e) => setTempBaseMonthCycleTime(e.target.value || null)} />
            </FormControl>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Box>
              {hasDraftBaseMonth && (
                <Button colorScheme="red" onClick={async (event) => {
                  event.currentTarget.blur();
                  await discardDraft("baseMonth");
                  onCloseBaseMonthModal();
                }}>{t('features.InvestmentScreening.ProductionProcess.button.discardDraft')}</Button>
              )}
            </Box>
            <Box display="flex">
              <Button colorScheme="blue" m={1} onClick={(event) => {
                event.currentTarget.blur();
                changeProductionLine("improvementBaseMonthYield", tempBaseMonthYield);
                changeProductionLine("improvementBaseMonthAvailability", tempBaseMonthAvailability);
                changeProductionLine("improvementBaseMonthCycleTime", tempBaseMonthCycleTime);

                // DBに一時保存
                saveDraft("baseMonth", JSON.stringify({
                  improvementBaseMonthYield: tempBaseMonthYield,
                  improvementBaseMonthAvailability: tempBaseMonthAvailability,
                  improvementBaseMonthCycleTime: tempBaseMonthCycleTime,
                }));
                setHasDraftBaseMonth(true);

                onCloseBaseMonthModal();
              }}>{t('common.button.confirmAction')}</Button>
              <Button m={1} onClick={(event) => {
                event.currentTarget.blur();
                onCloseBaseMonthModal();
              }}>{t('common.button.cancel')}</Button>
            </Box>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* パーツNo選択モーダル */}
      <Modal isOpen={isOpenSelectPartModal} onClose={() => { onCloseSelectPartModal() }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('features.InvestmentScreening.ProductionProcess.modal.partNoSelection')}
          </ModalHeader>
          <ModalBody>
            <Text>{t('features.InvestmentScreening.ProductionProcess.help.selectPartNo')}</Text>
            <VStack m={2} overflowY="auto">
              {partNoList.map(p => (
                <Button
                  key={p.partNo}
                  variant="link"
                  color="blue.500"
                  onClick={(event) => {
                    // フォーカスを外す
                    event.currentTarget.blur();
                    handleGoogleSpreadClick(`${p.partNo}`);
                  }}
                >{resolveDisplayPartNo(p)}
                </Button>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* ファイルアップロードモーダル */}
      <FileUploadModal
        isOpen={isOpenFileUploadModal}
        onClose={onCloseFileUploadModal}
        title={TITLE}
        projectId={projectId}
        folderName={"ProductionProcess"}
        allowedExtensions={allowedExtensions}
      />

      {/* BOMデータ一覧モーダル */}
      <BomInfoList
        projectId={projectId}
        partNo={activePartNo}
        displayPartNo={partNoList.find(p => p.partNo === activePartNo)?.displayPartNo}
        laborCostRate={null}
        overheadRate={null}
        startDate={projectWithRdsData?.projectStartDate}
        endDate={projectWithRdsData?.projectEndDate}
        currencyList={currencyData}
        pagePermissions={pagePermissions}
        onSave={async (bomSheetsData: BomSheetsData) => {

          // 初年度
          const projectStartDate = dayjs(projectWithRdsData?.projectStartDate);
          const firstYearMonth = projectStartDate.format("YYYY-MM");

          // 材料費データ保存
          const resultMaterialCostData = await saveMaterialCostData(bomSheetsData);

          // プロセスフローデータ保存
          const resultProcessData = await saveProcessData(bomSheetsData, firstYearMonth);

          // 最大サイクルタイムと歩留まり算出
          const { maxCycleTime, yieldRate } = calcCycleTimeAndYield(
            bomSheetsData.cycletime.dataRows.map(row => row.monthlyData),
            bomSheetsData.yield.dataRows.map(row => row.monthlyData),
            firstYearMonth
          );

          changeProductionLine("cycleTime", maxCycleTime);
          changeProductionLine("yieldRate", yieldRate);

          const bomRows = await getBomByProjectId(projectId, reviewDate);
          if (bomRows) {
            // BOMデータをStateに保持（パーツNo単位の登録チェックに使用）
            setBomDataList(bomRows);
          }

          return {
            resultMaterialCostData: resultMaterialCostData,
            resultProcessData: resultProcessData,
          }

        }}
        isOpen={isOpenBomInfoList}
        onClose={onCloseBomInfoList}

      />

      {/* 工程作成モーダル */}
      <BomCreateModal
        isOpen={isOpenBomCreateModal}
        onClose={onCloseBomCreateModal}
      />

      {/* 確認モーダル */}
      < Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="md" >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('features.InvestmentScreening.ProductionProcess.modal.confirmTitle', { title: TITLE })}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text whiteSpace="pre-wrap">{t('features.InvestmentScreening.ProductionProcess.help.confirmBody')}</Text>
            <VStack align="stretch" spacing={4} mt={2}>
              <Flex align="center" >
                <Text
                  fontWeight="bold"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  w={`${labelWidth}px`}
                >
                  {t('features.InvestmentScreening.ProductionProcess.field.productionLineNameLabel')}
                </Text>
                <Text style={confirmValueStyle} title={productionLine?.productionLineName ?? ""}>{productionLine?.productionLineName || t('features.InvestmentScreening.ProductionProcess.field.notSet')}</Text>
              </Flex>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={(e) => handleConfirm(e)}>
              {t('common.button.save')}
            </Button>
            <Button onClick={onCloseConfirmModal}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal >
    </Box >
  );
};

export default ProductionProcess;
